import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _taggedTemplateLiteral from '@babel/runtime/helpers/taggedTemplateLiteral';
import React, { useRef, useContext, createContext, forwardRef, useEffect, useState, useMemo, useCallback } from 'react';
import { Root as Root$1, Item, Header, Trigger as Trigger$1, Content as Content$1 } from '@radix-ui/react-accordion';
import { styled, useIsoLayoutEffect, keyframes, css, useReducedMotion, createFontFaceStyles, screenReaderOnly, breakpoint, debounce } from '@moonpig/launchpad-utils';
import { all, system, sizingSpacing, flexbox, spacing } from '@moonpig/launchpad-system';
import { IconSystemChevronDown, IconSystemLoader, IconSystemCross, IconInformation, IconInformationWarning, IconInformationSuccess, IconInformationError, IconSystemChevronRight, IconSystemArrowBack, IconSystemArrowForward, IconSystemCrossSmall, IconSystemHeart, IconSystemMenuOptions, Illustration404, IconSystemTick, IconSystemStar, IconSystemChevronLeft } from '@moonpig/launchpad-assets';
import { spacingPx, colorValue, theme } from '@moonpig/launchpad-theme';
import { useLocaleText } from '@moonpig/launchpad-localisation';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { createGlobalStyle } from 'styled-components';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _typeof from '@babel/runtime/helpers/typeof';
import * as RadixPopover from '@radix-ui/react-popover';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import * as Dialog from '@radix-ui/react-dialog';
import { Close, Root as Root$2, Portal, Overlay, Content as Content$2, Title, Description } from '@radix-ui/react-dialog';
import _objectDestructuringEmpty from '@babel/runtime/helpers/objectDestructuringEmpty';
import * as Tabs from '@radix-ui/react-tabs';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { createStore } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

var _templateObject$U;
var Box = styled.div(_templateObject$U || (_templateObject$U = _taggedTemplateLiteral(["\n  ", "\n"])), all);

var ANIMATION_DURATION$1 = 300;
var ANIMATION_BUFFER = 50;
var useForceMountAnimation = function useForceMountAnimation(_ref) {
  var contentRef = _ref.contentRef,
    forceMount = _ref.forceMount;
  useIsoLayoutEffect(function () {
    if (!forceMount || !contentRef.current) return undefined;
    var accordionItem = contentRef.current.closest('[data-state]');
    if (!accordionItem) return undefined;
    var updateStyles = function updateStyles() {
      if (!contentRef.current) return;
      var isOpen = accordionItem.getAttribute('data-state') === 'open';
      var scrollHeight = contentRef.current.scrollHeight;
      contentRef.current.style.setProperty('--accordion-content-height', "".concat(scrollHeight, "px"));
      contentRef.current.style.setProperty('--accordion-max-height', isOpen ? "".concat(scrollHeight, "px") : '0px');
      contentRef.current.style.setProperty('--accordion-opacity', isOpen ? '1' : '0');
      contentRef.current.style.setProperty('--accordion-visibility', isOpen ? 'visible' : 'hidden');
    };
    var rafId = null;
    var timeoutId = null;
    var scheduleUpdate = function scheduleUpdate() {
      var waitForAnimation = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      if (waitForAnimation) {
        timeoutId = setTimeout(function () {
          rafId = requestAnimationFrame(function () {
            rafId = requestAnimationFrame(updateStyles);
          });
        }, ANIMATION_DURATION$1 + ANIMATION_BUFFER);
      } else {
        rafId = requestAnimationFrame(function () {
          rafId = requestAnimationFrame(updateStyles);
        });
      }
    };
    updateStyles();
    var mutationObserver = new MutationObserver(function () {
      return scheduleUpdate();
    });
    mutationObserver.observe(accordionItem, {
      attributes: true,
      attributeFilter: ['data-state']
    });

    // Wait for inner accordion content animations to finish before updating styles
    var contentMutationObserver = new MutationObserver(function () {
      return scheduleUpdate(true);
    });
    contentMutationObserver.observe(contentRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state', 'style']
    });
    var resizeObserver = new ResizeObserver(function () {
      return scheduleUpdate();
    });
    resizeObserver.observe(contentRef.current);
    return function () {
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      mutationObserver.disconnect();
      contentMutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [forceMount, contentRef]);
};

var _excluded$v = ["children", "bgcolor"];
var _templateObject$T, _templateObject2$u, _templateObject3$g, _templateObject4$5, _templateObject5$5, _templateObject6$2, _templateObject7$2, _templateObject8$1, _templateObject9$1, _templateObject0$1;
var ANIMATION_DURATION = 300;
var REDUCED_MOTION_ANIMATION_DURATION = 1200;
var INDENT_SPACING = 6;
var slideDownAnimation = keyframes(_templateObject$T || (_templateObject$T = _taggedTemplateLiteral(["\n  0% { height: 0 }\n  100% { height: var(--radix-accordion-content-height); }\n"])));
var slideUpAnimation = keyframes(_templateObject2$u || (_templateObject2$u = _taggedTemplateLiteral(["\n  0% { height: var(--radix-accordion-content-height); }\n  100% { height: 0; }\n"])));
var StyledChevron = styled(IconSystemChevronDown)(_templateObject3$g || (_templateObject3$g = _taggedTemplateLiteral(["\n  transition: transform ", "ms;\n\n  @media (prefers-reduced-motion) {\n    transition: transform ", "ms;\n  }\n"])), ANIMATION_DURATION, REDUCED_MOTION_ANIMATION_DURATION);
var StyledIconContainer = styled.div(_templateObject4$5 || (_templateObject4$5 = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  width: '44px',
  height: '44px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));
var StyledAccordion = styled(Root$1)(_templateObject5$5 || (_templateObject5$5 = _taggedTemplateLiteral(["\n  width: 100%;\n  ", "\n\n  ", "\n"])), function (_ref) {
  var $bgcolor = _ref.$bgcolor;
  return system({
    bgcolor: $bgcolor || 'colorBackground01'
  });
}, system({
  borderColor: 'colorBlack30',
  borderTop: 1,
  borderBottom: 1
}));
var StyledAccordionItem = styled(Item)(_templateObject6$2 || (_templateObject6$2 = _taggedTemplateLiteral(["\n  overflow: hidden;\n  ", "\n\n  & + & {\n    ", "\n  }\n\n  &:focus-within {\n    position: relative;\n    z-index: 1;\n\n    ", ";\n  }\n"])), function (_ref2) {
  var $bgcolor = _ref2.$bgcolor;
  return system({
    bgcolor: $bgcolor || 'inherit'
  });
}, system({
  borderColor: 'colorBlack30',
  borderTop: 1
}), system({
  borderColor: 'transparent',
  boxShadow: 5,
  borderRadius: 1
}));
var StyledAccordionHeader = styled(Header)(_templateObject7$2 || (_templateObject7$2 = _taggedTemplateLiteral(["\n  margin: 0;\n"])));
var StyledAccordionTrigger = styled(Trigger$1)(_templateObject8$1 || (_templateObject8$1 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  width: 100%;\n\n  ", "\n\n  &[data-disabled] {\n    cursor: not-allowed;\n\n    ", "\n\n    ", " {\n      display: none;\n    }\n  }\n\n  &:not([data-disabled]) {\n    &:hover,\n    &:focus {\n      ", "\n    }\n\n    &:focus {\n      outline: none;\n    }\n\n    &[data-state='open'] {\n      ", "\n\n      &:hover,\n      &:focus {\n        ", "\n      }\n    }\n\n    &[data-state='open'] ", " {\n      transform: rotate(180deg);\n    }\n  }\n"])), system({
  color: 'colorTextHeadline',
  typography: {
    xs: 'typeMobileDisplay03',
    md: 'typeDesktopDisplay05'
  },
  px: INDENT_SPACING,
  py: 5
}), system({
  bgcolor: 'colorBackgroundDeactivated',
  color: 'colorInteractionDeactivated',
  borderColor: 'colorInteractionDeactivated'
}), StyledChevron, system({
  color: 'colorInteractionHoverState'
}), system({
  color: 'colorInteractionSelectedState'
}), system({
  color: 'colorInteractionHoverState'
}), StyledChevron);
var StyledAccordionContent = styled.div(_templateObject9$1 || (_templateObject9$1 = _taggedTemplateLiteral(["\n  overflow: hidden;\n\n  ", "\n"])), function (_ref3) {
  var $hasForceMount = _ref3.$hasForceMount;
  return $hasForceMount && "\n      max-height: var(--accordion-max-height, 0px);\n      opacity: var(--accordion-opacity, 0);\n      visibility: var(--accordion-visibility, hidden);\n      \n      transition: max-height ".concat(ANIMATION_DURATION, "ms cubic-bezier(0.87, 0, 0.13, 1),\n                  opacity ").concat(ANIMATION_DURATION, "ms cubic-bezier(0.87, 0, 0.13, 1),\n                  visibility ").concat(ANIMATION_DURATION, "ms cubic-bezier(0.87, 0, 0.13, 1);\n\n      @media (prefers-reduced-motion) {\n        transition: max-height ").concat(REDUCED_MOTION_ANIMATION_DURATION, "ms linear,\n                    opacity ").concat(REDUCED_MOTION_ANIMATION_DURATION, "ms linear,\n                    visibility ").concat(REDUCED_MOTION_ANIMATION_DURATION, "ms linear;\n      }\n    ");
});
var StyledRadixAccordionContent = styled(Content$1)(_templateObject0$1 || (_templateObject0$1 = _taggedTemplateLiteral(["\n  &[data-state='open'] {\n    animation: ", " ", "ms\n      cubic-bezier(0.87, 0, 0.13, 1);\n\n    @media (prefers-reduced-motion) {\n      animation: ", " ", "ms\n        linear;\n    }\n  }\n\n  &[data-state='closed'] {\n    animation: ", " ", "ms\n      cubic-bezier(0.87, 0, 0.13, 1);\n\n    @media (prefers-reduced-motion) {\n      animation: ", " ", "ms\n        linear;\n    }\n  }\n"])), slideDownAnimation, ANIMATION_DURATION, slideDownAnimation, REDUCED_MOTION_ANIMATION_DURATION, slideUpAnimation, ANIMATION_DURATION, slideUpAnimation, REDUCED_MOTION_ANIMATION_DURATION);
var Accordion = function Accordion(_ref4) {
  var children = _ref4.children,
    bgcolor = _ref4.bgcolor,
    rest = _objectWithoutProperties(_ref4, _excluded$v);
  return /*#__PURE__*/React.createElement(StyledAccordion, _extends({
    $bgcolor: bgcolor
  }, rest), children);
};
var AccordionContent = function AccordionContent(_ref5) {
  var heading = _ref5.heading,
    value = _ref5.value,
    bgcolor = _ref5.bgcolor,
    disabled = _ref5.disabled,
    children = _ref5.children,
    _ref5$forceMount = _ref5.forceMount,
    forceMount = _ref5$forceMount === void 0 ? false : _ref5$forceMount;
  var contentRef = useRef(null);
  useForceMountAnimation({
    contentRef: contentRef,
    forceMount: forceMount
  });
  return /*#__PURE__*/React.createElement(StyledAccordionItem, {
    value: value,
    disabled: disabled,
    $bgcolor: bgcolor
  }, heading, forceMount ? /*#__PURE__*/React.createElement(StyledAccordionContent, {
    $hasForceMount: forceMount,
    ref: contentRef
  }, /*#__PURE__*/React.createElement(Box, {
    px: INDENT_SPACING,
    pb: 6
  }, children)) : /*#__PURE__*/React.createElement(StyledRadixAccordionContent, null, /*#__PURE__*/React.createElement(Box, {
    px: INDENT_SPACING,
    pb: 6
  }, children)));
};
var AccordionHeading = function AccordionHeading(_ref6) {
  var children = _ref6.children,
    level = _ref6.level;
  return /*#__PURE__*/React.createElement(StyledAccordionHeader, {
    as: level
  }, /*#__PURE__*/React.createElement(StyledAccordionTrigger, null, /*#__PURE__*/React.createElement("div", null, children), /*#__PURE__*/React.createElement(StyledIconContainer, null, /*#__PURE__*/React.createElement(StyledChevron, {
    width: "1.5rem",
    height: "1.5rem",
    "aria-hidden": true
  }))));
};

var Flex = styled(Box)({
  display: 'flex'
});

var _excluded$u = ["children"];
var _templateObject$S;
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var LinkContext = /*#__PURE__*/createContext('a');
var DefaultLink = styled.a(_templateObject$S || (_templateObject$S = _taggedTemplateLiteral(["\n  cursor: pointer;\n"])));
var Link = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded$u);
  var LinkAsComponent = useContext(LinkContext);
  var LinkComponent = LinkAsComponent !== 'a' ? LinkAsComponent : DefaultLink;
  return /*#__PURE__*/React.createElement(LinkComponent, _extends({}, rest, {
    ref: ref,
    style: _objectSpread$1({
      cursor: 'pointer'
    }, rest.style)
  }), children);
});
Link.displayName = 'Link';
var LinkAsProvider = function LinkAsProvider(_ref2) {
  var children = _ref2.children,
    linkAs = _ref2.linkAs;
  return /*#__PURE__*/React.createElement(LinkContext.Provider, {
    value: linkAs
  }, children);
};

var _templateObject$R, _templateObject2$t, _templateObject3$f;
var focusIndicatorStyles = css(_templateObject$R || (_templateObject$R = _taggedTemplateLiteral(["\n  transition: box-shadow 150ms linear;\n\n  @media (prefers-reduced-motion) {\n    transition: none;\n  }\n\n  &:disabled {\n    ", "\n  }\n\n  &:not(:disabled) {\n    &:focus {\n      outline: transparent;\n\n      ", "\n    }\n\n    &:focus:not(:focus-visible) {\n      ", "\n    }\n  }\n"])), system({
  boxShadow: 0
}), system({
  boxShadow: 5
}), system({
  boxShadow: 0
}));
var StyledButton$4 = styled.button(_templateObject2$t || (_templateObject2$t = _taggedTemplateLiteral(["\n  ", "\n"])), focusIndicatorStyles);
var StyledLink$3 = styled(Link)(_templateObject3$f || (_templateObject3$f = _taggedTemplateLiteral(["\n  ", "\n"])), focusIndicatorStyles);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var isLink = function isLink(props) {
  return 'href' in props;
};
var FocusIndicator = /*#__PURE__*/forwardRef(function (props, ref) {
  if (isLink(props)) {
    return /*#__PURE__*/React.createElement(StyledLink$3, _extends({
      ref: ref
    }, props));
  }
  return /*#__PURE__*/React.createElement(StyledButton$4, _extends({
    ref: ref
  }, props));
});
FocusIndicator.displayName = 'FocusIndicator';

var _excluded$t = ["icon", "label", "onClick", "disabled", "showNotificationBadge", "colours"];
var _templateObject$Q, _templateObject2$s;
var COMPONENT_SIZE = '48px';
var BADGE_SIZE_PX = '10px';
var notificationBadgeStyle = css(_templateObject$Q || (_templateObject$Q = _taggedTemplateLiteral(["\n  &::after {\n    content: '';\n    width: ", ";\n    height: ", ";\n    display: inline-block;\n    ", "\n  }\n  &:disabled::after {\n    ", "\n  }\n"])), BADGE_SIZE_PX, BADGE_SIZE_PX, system({
  bgcolor: 'colorFeedbackError',
  borderRadius: 2,
  position: 'absolute',
  margin: '0 0 16px 16px'
}), system({
  bgcolor: 'colorInteractionDeactivated'
}));
var StyledIconButton$1 = styled.button(_templateObject2$s || (_templateObject2$s = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: ", ";\n  height: ", ";\n  cursor: pointer;\n\n  ", "\n\n  &:disabled {\n    ", ";\n    cursor: not-allowed;\n  }\n\n  &:not(:disabled) {\n    &:hover {\n      ", "\n    }\n\n    &:focus {\n      ", "\n\n      &:hover {\n        ", "\n      }\n    }\n\n    &:active {\n      ", "\n    }\n  }\n\n  ", "\n\n  ", "\n"])), COMPONENT_SIZE, COMPONENT_SIZE, function (_ref) {
  var colours = _ref.colours;
  return system({
    borderRadius: 2,
    color: (colours === null || colours === void 0 ? void 0 : colours["default"]) && colours["default"]
  });
}, system({
  color: 'colorInteractionDeactivated'
}), function (_ref2) {
  var colours = _ref2.colours;
  return system({
    color: (colours === null || colours === void 0 ? void 0 : colours.hover) && colours.hover
  });
}, function (_ref3) {
  var colours = _ref3.colours;
  return system({
    color: (colours === null || colours === void 0 ? void 0 : colours.focus) && colours.focus
  });
}, function (_ref4) {
  var colours = _ref4.colours;
  return system({
    color: (colours === null || colours === void 0 ? void 0 : colours.hover) && colours.hover
  });
}, function (_ref5) {
  var colours = _ref5.colours;
  return system({
    color: (colours === null || colours === void 0 ? void 0 : colours.active) && colours.active
  });
}, function (_ref6) {
  var addbadge = _ref6.addbadge;
  return addbadge && notificationBadgeStyle;
}, focusIndicatorStyles);
var defaultColours = {
  "default": 'colorInteractionIcon',
  hover: 'colorInteractionIconHover',
  focus: 'colorInteractionSelectedState',
  active: 'colorInteractionSelectedState'
};
var IconButton = /*#__PURE__*/forwardRef(function (_ref7, ref) {
  var icon = _ref7.icon,
    label = _ref7.label,
    onClick = _ref7.onClick,
    disabled = _ref7.disabled,
    showNotificationBadge = _ref7.showNotificationBadge,
    _ref7$colours = _ref7.colours,
    colours = _ref7$colours === void 0 ? defaultColours : _ref7$colours,
    props = _objectWithoutProperties(_ref7, _excluded$t);
  var Icon = icon;
  var t = useLocaleText();
  var screenreaderNotificationLabel = showNotificationBadge ? "".concat(t('icon_button.notification_label')) : '';
  var ariaLabel = "".concat(label).concat(screenreaderNotificationLabel);
  return /*#__PURE__*/React.createElement(StyledIconButton$1, _extends({
    ref: ref,
    disabled: disabled,
    onClick: onClick,
    "aria-label": ariaLabel,
    addbadge: showNotificationBadge ? true : undefined,
    colours: colours
  }, props), /*#__PURE__*/React.createElement(Icon, {
    width: "28px",
    height: "28px",
    "aria-hidden": true
  }));
});
IconButton.displayName = 'IconButton';

var _templateObject$P;
var COMPONENT_HEIGHT = spacingPx(12);
var commonStyles$1 = css(_templateObject$P || (_templateObject$P = _taggedTemplateLiteral(["\n  position: relative;\n  display: inline-flex;\n  flex-direction: row;\n  align-items: center;\n  justify-content: center;\n  min-height: ", ";\n  text-align: center;\n  transition: background-color 150ms linear;\n  cursor: pointer;\n\n  ", "\n\n  &:disabled {\n    cursor: not-allowed;\n\n    ", "\n  }\n\n  &:not(:disabled) {\n    &:hover {\n      ", "\n    }\n\n    &:active {\n      ", "\n    }\n  }\n\n  ", "\n"])), COMPONENT_HEIGHT, system({
  p: 5,
  typography: 'typeButtonLabel',
  borderRadius: 2,
  boxShadow: 1
}), system({
  bgcolor: 'colorBackgroundDeactivated',
  color: 'colorInteractionDeactivated',
  borderColor: 'colorInteractionDeactivated'
}), system({
  boxShadow: 2
}), system({
  boxShadow: 1
}), focusIndicatorStyles);

var _excluded$s = ["label", "color", "size"];
var _templateObject$O, _templateObject2$r, _templateObject3$e;
var DEFAULT_COLOR = 'colorInteractionIcon';
var DEFAULT_COMPONENT_SIZE = 12;

/* stylelint-disable property-no-vendor-prefix */
var StyledProgressIndicator = styled.progress(_templateObject$O || (_templateObject$O = _taggedTemplateLiteral(["\n  &:indeterminate {\n    -webkit-appearance: none;\n    -moz-appearance: none;\n    appearance: none;\n    background: none;\n    border: 0;\n    width: 0;\n    height: 0;\n\n    &::-moz-progress-bar {\n      background: none;\n    }\n\n    &::-webkit-progress-bar {\n      display: none;\n    }\n\n    &::-ms-fill {\n      animation-name: none;\n    }\n  }\n"])));
var loaderRotation = keyframes(_templateObject2$r || (_templateObject2$r = _taggedTemplateLiteral(["\n  0% { transform: rotateZ(0deg) }\n  100% { transform: rotateZ(360deg) }\n"])));
var StyledLoadingIndicator = styled(IconSystemLoader)(_templateObject3$e || (_templateObject3$e = _taggedTemplateLiteral(["\n  ", ";\n\n  animation: ", " infinite linear;\n\n  &.reduce-motion {\n    animation-duration: 4000ms;\n  }\n\n  animation-duration: 2000ms;\n"])), function (_ref) {
  var theme = _ref.theme,
    color = _ref.color,
    size = _ref.size;
  return system({
    color: color,
    width: "".concat(theme.spacing[size], "px"),
    height: "".concat(theme.spacing[size], "px")
  });
}, loaderRotation);
var LoadingIndicator = function LoadingIndicator(_ref2) {
  var label = _ref2.label,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? DEFAULT_COLOR : _ref2$color,
    _ref2$size = _ref2.size,
    size = _ref2$size === void 0 ? DEFAULT_COMPONENT_SIZE : _ref2$size,
    props = _objectWithoutProperties(_ref2, _excluded$s);
  var reduceMotion = useReducedMotion();
  var t = useLocaleText();
  var defaultLabel = t('common.loading');
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledProgressIndicator, {
    id: "progress-indicator",
    "aria-label": label || defaultLabel
  }), /*#__PURE__*/React.createElement(StyledLoadingIndicator, _extends({}, props, {
    "aria-labelledby": 'progress-indicator',
    color: color,
    size: size,
    className: reduceMotion ? 'reduce-motion' : '',
    "data-testid": "loading-indicator"
  })));
};

var _templateObject$N;
var SPINNER_SIZE = spacingPx(8);
var LoadingIndicatorContainer = styled.span(_templateObject$N || (_templateObject$N = _taggedTemplateLiteral(["\n  position: relative;\n  align-self: center;\n  width: ", ";\n  height: ", ";\n"])), SPINNER_SIZE, SPINNER_SIZE);
var ButtonLoadingIndicator = function ButtonLoadingIndicator() {
  return /*#__PURE__*/React.createElement(LoadingIndicatorContainer, null, /*#__PURE__*/React.createElement(LoadingIndicator, {
    size: 8,
    color: "colorInteractionLoading",
    role: "presentation"
  }));
};

var _templateObject$M;
var ButtonContentContainer = styled.span(_templateObject$M || (_templateObject$M = _taggedTemplateLiteral(["\n  display: flex;\n  gap: 8px;\n"])));

var _templateObject$L;
var StyledIcon$1 = styled.span(_templateObject$L || (_templateObject$L = _taggedTemplateLiteral(["\n  position: relative;\n  align-self: center;\n  ", "\n"])), system({
  width: 'iconMedium',
  height: 'iconMedium'
}));
var ButtonIconContainer = function ButtonIconContainer(_ref) {
  var icon = _ref.icon,
    testId = _ref.testId;
  return /*#__PURE__*/React.createElement(StyledIcon$1, {
    "data-testid": testId,
    role: "presentation"
  }, icon);
};

var _excluded$r = ["children", "testId", "inline", "loading", "leadingIcon", "trailingIcon"];
var _templateObject$K, _templateObject2$q, _templateObject3$d;
var tertiaryButtonStyles = css(_templateObject$K || (_templateObject$K = _taggedTemplateLiteral(["\n  ", "\n\n  text-decoration-skip-ink: none;\n  background-color: transparent;\n\n  ", "\n\n  ", "\n\n  ", "\n\n  &.loading {\n    cursor: progress;\n    ", "\n  }\n\n  &:disabled {\n    border: solid 2px transparent;\n    background-color: transparent;\n  }\n\n  &:not(:disabled, .loading) {\n    &:hover,\n    &:focus {\n      text-decoration-thickness: 2px;\n    }\n\n    &:hover {\n      ", "\n    }\n\n    &:focus {\n      ", "\n    }\n\n    &:active {\n      ", "\n    }\n  }\n"])), commonStyles$1, system({
  px: 0,
  typography: 'typeButtonUnderlined',
  color: 'colorInteractionButton',
  border: 2,
  borderColor: 'transparent',
  boxShadow: 0
}), function (_ref) {
  var inline = _ref.inline;
  return inline && system({
    py: 0,
    minHeight: 'auto'
  });
}, sizingSpacing, system({
  color: 'colorInteractionLoading'
}), system({
  color: 'colorInteractionButtonHover',
  boxShadow: 0
}), system({
  color: 'colorInteractionButtonFocus'
}), system({
  boxShadow: 0
}));
var StyledButton$3 = styled.button(_templateObject2$q || (_templateObject2$q = _taggedTemplateLiteral(["\n  ", "\n"])), tertiaryButtonStyles);
var StyledLink$2 = styled(Link)(_templateObject3$d || (_templateObject3$d = _taggedTemplateLiteral(["\n  ", "\n"])), tertiaryButtonStyles);
var TertiaryButton = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
    _props$testId = props.testId,
    testId = _props$testId === void 0 ? 'lp-components-tertiary-button' : _props$testId,
    _props$inline = props.inline,
    inline = _props$inline === void 0 ? false : _props$inline,
    loading = props.loading,
    leadingIcon = props.leadingIcon,
    trailingIcon = props.trailingIcon,
    rest = _objectWithoutProperties(props, _excluded$r);
  var t = useLocaleText();
  var ariaLive = loading ? 'polite' : 'off';
  var ariaLabel = loading ? t('common.loading') : undefined;
  var descendants = /*#__PURE__*/React.createElement(ButtonContentContainer, null, loading && /*#__PURE__*/React.createElement(ButtonLoadingIndicator, null), !loading && leadingIcon && /*#__PURE__*/React.createElement(ButtonIconContainer, {
    icon: leadingIcon,
    testId: "".concat(testId, "-leading-icon")
  }), children, trailingIcon && /*#__PURE__*/React.createElement(ButtonIconContainer, {
    icon: trailingIcon,
    testId: "".concat(testId, "-trailing-icon")
  }));
  if ('href' in props) {
    return /*#__PURE__*/React.createElement(StyledLink$2, _extends({
      ref: ref,
      "data-testid": testId,
      inline: inline,
      "aria-live": ariaLive,
      "aria-busy": !!loading,
      disabled: !!loading,
      "aria-label": ariaLabel,
      style: {
        cursor: loading ? 'progress' : 'pointer'
      },
      className: loading ? 'loading' : ''
    }, rest), descendants);
  }
  return /*#__PURE__*/React.createElement(StyledButton$3, _extends({
    ref: ref,
    "data-testid": testId,
    inline: inline,
    "aria-live": ariaLive,
    "aria-busy": !!loading,
    disabled: !!loading,
    "aria-label": ariaLabel,
    className: loading ? 'loading' : ''
  }, rest), descendants);
});
TertiaryButton.displayName = 'TertiaryButton';

var _excluded$q = ["children", "variant", "icon", "onClickClose", "actions", "testId", "labels", "ariaLive", "autoFocus", "triggerRef"];
var _templateObject$J, _templateObject2$p;
var variantsMap$1 = {
  error: {
    bgColor: 'colorBackgroundError',
    color: 'colorFeedbackError',
    iconColor: 'colorFeedbackError'
  },
  warning: {
    bgColor: 'colorBackgroundWarning',
    color: 'colorFeedbackWarning',
    iconColor: 'colorFeedbackWarning'
  },
  success: {
    bgColor: 'colorBackgroundSuccess',
    color: 'colorFeedbackSuccess',
    iconColor: 'colorFeedbackSuccess'
  },
  info: {
    bgColor: 'colorBackgroundInformation',
    color: 'colorFeedbackInformation',
    iconColor: 'colorFeedbackInformation'
  }
};
var StyledAlert = styled(Flex)(_templateObject$J || (_templateObject$J = _taggedTemplateLiteral(["\n  color: inherit;\n  position: relative;\n  ", ";\n  ", ";\n"])), function (_ref) {
  var bgColor = _ref.bgColor,
    color = _ref.color;
  return system({
    py: 6,
    pr: 10,
    bgcolor: bgColor,
    border: 2,
    borderColor: color,
    borderRadius: 2
  });
}, sizingSpacing);
var StyledIconButton = styled(IconButton)(_templateObject2$p || (_templateObject2$p = _taggedTemplateLiteral(["\n  position: absolute;\n  right: ", ";\n  height: ", ";\n  width: ", ";\n"])), spacingPx(5), spacingPx(9), spacingPx(9));

// eslint-disable-next-line no-use-before-define
var getIcon = function getIcon(variant, icon) {
  if (icon) {
    return icon;
  }
  switch (variant) {
    case 'error':
      {
        return IconInformationError;
      }
    case 'success':
      {
        return IconInformationSuccess;
      }
    case 'warning':
      {
        return IconInformationWarning;
      }
    default:
      {
        return IconInformation;
      }
  }
};
var Alert = function Alert(_ref2) {
  var children = _ref2.children,
    variant = _ref2.variant,
    icon = _ref2.icon,
    onClickClose = _ref2.onClickClose,
    actions = _ref2.actions,
    _ref2$testId = _ref2.testId,
    testId = _ref2$testId === void 0 ? 'lp-components-alert' : _ref2$testId,
    labels = _ref2.labels,
    _ref2$ariaLive = _ref2.ariaLive,
    ariaLive = _ref2$ariaLive === void 0 ? 'polite' : _ref2$ariaLive,
    _ref2$autoFocus = _ref2.autoFocus,
    autoFocus = _ref2$autoFocus === void 0 ? false : _ref2$autoFocus,
    triggerRef = _ref2.triggerRef,
    props = _objectWithoutProperties(_ref2, _excluded$q);
  var _variantsMap$variant = variantsMap$1[variant],
    bgColor = _variantsMap$variant.bgColor,
    color = _variantsMap$variant.color,
    iconColor = _variantsMap$variant.iconColor;
  var IconStart = getIcon(variant, icon);
  var t = useLocaleText();
  var alertRef = useRef(null);
  useEffect(function () {
    if (autoFocus && alertRef.current) {
      alertRef.current.focus();
      alertRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [autoFocus]);
  var restoreFocus = function restoreFocus() {
    var focusTarget = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current;
    if (focusTarget) {
      requestAnimationFrame(function () {
        focusTarget.focus();
      });
    }
  };
  return /*#__PURE__*/React.createElement(StyledAlert, _extends({}, props, {
    bgColor: bgColor,
    color: color,
    "data-testid": testId,
    role: "alert",
    "aria-live": ariaLive,
    tabIndex: autoFocus ? -1 : undefined,
    ref: alertRef
  }), /*#__PURE__*/React.createElement(Box, {
    color: iconColor,
    pl: 6
  }, /*#__PURE__*/React.createElement(IconStart, {
    width: "28px",
    height: "28px",
    display: "block",
    fill: iconColor,
    role: "presentation"
  })), /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    px: 4,
    alignSelf: "center"
  }, children, actions && /*#__PURE__*/React.createElement(Box, {
    mt: 4
  }, actions.map(function (_ref3) {
    var title = _ref3.title,
      onClick = _ref3.onClick;
    return /*#__PURE__*/React.createElement(TertiaryButton, {
      key: title,
      inline: true,
      mr: 8,
      onClick: onClick
    }, title);
  }))), onClickClose && /*#__PURE__*/React.createElement(StyledIconButton, {
    icon: IconSystemCross,
    label: (labels === null || labels === void 0 ? void 0 : labels.close) || t('common.close'),
    onClick: function onClick(e) {
      onClickClose(e);
      restoreFocus();
    },
    type: "button"
  }));
};

var _excluded$p = ["children", "ratio"];
var _templateObject$I;
var StyledAspectRatio = styled.div(_templateObject$I || (_templateObject$I = _taggedTemplateLiteral(["\n  position: relative;\n  ", ";\n\n  &::before {\n    display: block;\n    height: 0;\n    content: '';\n    padding-bottom: ", ";\n  }\n\n  img {\n    display: block;\n  }\n\n  > :first-child:not(picture),\n  > picture img {\n    position: absolute;\n\n    top: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n  }\n\n  > figure :first-child:not(picture),\n  > figure picture img {\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n  }\n"])), sizingSpacing, function (_ref) {
  var _ref$ratio = _ref.ratio,
    ratio = _ref$ratio === void 0 ? 1 : _ref$ratio;
  return "".concat(1 / ratio * 100, "%");
});
var AspectRatio = function AspectRatio(_ref2) {
  var children = _ref2.children,
    ratio = _ref2.ratio,
    props = _objectWithoutProperties(_ref2, _excluded$p);
  return /*#__PURE__*/React.createElement(StyledAspectRatio, _extends({
    ratio: ratio
  }, props), children);
};

var _excluded$o = ["children", "typography"];
var _templateObject$H;
var StyledText = styled.span(_templateObject$H || (_templateObject$H = _taggedTemplateLiteral(["\n  ", "\n"])), all);

// Omits color as it clashes with MUI's pallete

var Text = function Text(_ref) {
  var children = _ref.children,
    _ref$typography = _ref.typography,
    typography = _ref$typography === void 0 ? 'typeBodyText' : _ref$typography,
    rest = _objectWithoutProperties(_ref, _excluded$o);
  return /*#__PURE__*/React.createElement(StyledText, _extends({
    typography: typography
  }, rest), children);
};

var _excluded$n = ["children", "padding", "limitWidth", "free"];
var _templateObject$G, _templateObject2$o, _templateObject3$c;
var defaultPadding = {
  xs: 6,
  md: 8
};
var applyPadding = function applyPadding(props) {
  var px = props.padding !== undefined ? props.padding : defaultPadding;
  return system({
    px: px
  });
};
var applyLimitWidth = function applyLimitWidth(props) {
  if (props.limitWidth) {
    return css(_templateObject$G || (_templateObject$G = _taggedTemplateLiteral(["\n      ", ";\n      box-sizing: content-box;\n      > div {\n        box-sizing: border-box;\n      }\n    "])), function (_ref) {
      var siteWidth = _ref.theme.siteWidth;
      return system({
        maxWidth: siteWidth
      });
    });
  }
  return undefined;
};
var applyFree = function applyFree(props) {
  if (!props.free) {
    return css(_templateObject2$o || (_templateObject2$o = _taggedTemplateLiteral(["\n      margin-left: auto;\n      margin-right: auto;\n    "])));
  }
  return undefined;
};
var StyledContainer$2 = styled.div(_templateObject3$c || (_templateObject3$c = _taggedTemplateLiteral(["\n  ", "\n  ", "\n  ", "\n"])), applyPadding, applyLimitWidth, applyFree);
var Container = function Container(_ref2) {
  var children = _ref2.children,
    padding = _ref2.padding,
    _ref2$limitWidth = _ref2.limitWidth,
    limitWidth = _ref2$limitWidth === void 0 ? false : _ref2$limitWidth,
    _ref2$free = _ref2.free,
    free = _ref2$free === void 0 ? false : _ref2$free,
    rest = _objectWithoutProperties(_ref2, _excluded$n);
  return /*#__PURE__*/React.createElement(StyledContainer$2, _extends({
    limitWidth: limitWidth,
    free: free,
    padding: padding
  }, rest), /*#__PURE__*/React.createElement("div", null, children));
};

var _templateObject$F, _templateObject2$n, _templateObject3$b;
var GlobalBanner = styled.div(_templateObject$F || (_templateObject$F = _taggedTemplateLiteral(["\n  width: 100%;\n  ", "\n"])), system({
  bgcolor: 'colorBackgroundInformation',
  py: 6,
  px: {
    xs: 6,
    md: '102px'
  }
}));
var ContainedBanner = styled.div(_templateObject2$n || (_templateObject2$n = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  bgcolor: 'colorBackground06',
  p: {
    xs: 6,
    md: 8
  }
}));
var FullWidthBanner = styled.div(_templateObject3$b || (_templateObject3$b = _taggedTemplateLiteral(["\n  width: 100%;\n  ", "\n"])), system({
  bgcolor: 'colorBackground05',
  py: 6,
  px: {
    xs: 6,
    md: '102px'
  }
}));
var BannerContent = function BannerContent(_ref) {
  var heading = _ref.heading,
    cta = _ref.cta,
    body = _ref.body,
    image = _ref.image,
    variant = _ref.variant;
  var BannerText = function BannerText() {
    return /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column",
      mr: {
        xs: 4,
        md: 5
      },
      flexGrow: cta ? 4 : 0,
      textAlign: cta || image ? 'start' : 'center',
      justifyContent: "center"
    }, /*#__PURE__*/React.createElement(Text, {
      typography: "typeBodyLabel",
      color: "colorTextHeadline"
    }, heading), body && /*#__PURE__*/React.createElement(Text, {
      typography: "typeBodyText",
      color: "colorTextBody"
    }, body));
  };
  var BannerCta = function BannerCta() {
    return /*#__PURE__*/React.createElement(Flex, {
      height: '30px',
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(TertiaryButton, {
      p: 0,
      trailingIcon: /*#__PURE__*/React.createElement(IconSystemChevronRight, null),
      href: cta === null || cta === void 0 ? void 0 : cta.href,
      onClick: cta === null || cta === void 0 ? void 0 : cta.onClick
    }, cta === null || cta === void 0 ? void 0 : cta.title));
  };
  return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "row",
    width: '100%',
    gap: '8px',
    justifyContent: cta ? 'start' : 'center',
    "data-testid": "lp-components-banner-content"
  }, image && /*#__PURE__*/React.cloneElement(image, {
    'data-testid': 'lp-components-banner-image',
    role: 'presentation'
  }), cta ? /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "row",
    alignItems: "center",
    width: '100%',
    flexWrap: variant === 'contained' ? 'wrap' : 'nowrap'
  }, /*#__PURE__*/React.createElement(BannerText, null), /*#__PURE__*/React.createElement(BannerCta, null)) : /*#__PURE__*/React.createElement(BannerText, null)));
};
var Banner = function Banner(props) {
  var variant = props.variant;
  if (variant === 'global') {
    return /*#__PURE__*/React.createElement(GlobalBanner, null, /*#__PURE__*/React.createElement(BannerContent, props));
  }
  if (variant === 'full-width') {
    return /*#__PURE__*/React.createElement(FullWidthBanner, null, /*#__PURE__*/React.createElement(BannerContent, props));
  }
  return /*#__PURE__*/React.createElement(ContainedBanner, null, /*#__PURE__*/React.createElement(BannerContent, props));
};

var _templateObject$E;
var commonStyles = css(_templateObject$E || (_templateObject$E = _taggedTemplateLiteral(["\n  ", "\n\n  & > li {\n    ", "\n  }\n\n  & li,\n  & ol {\n    ", "\n  }\n"])), system({
  typography: 'typeBodyText',
  color: 'colorTextBody',
  ml: 7
}), system({
  pl: 6
}), system({
  my: 6
}));

var _templateObject$D;
var StyledUnorderedList = styled.ul(_templateObject$D || (_templateObject$D = _taggedTemplateLiteral(["\n  ", "\n\n  ", "\n\n  & > li > ul {\n    list-style-type: ", ";\n\n    & > li > ul {\n      list-style-type: ", ";\n    }\n  }\n"])), commonStyles, function (_ref) {
  var removeMarkers = _ref.removeMarkers;
  return !removeMarkers && "list-style-type: disc;";
}, function (_ref2) {
  var removeMarkers = _ref2.removeMarkers;
  return removeMarkers ? "none" : "circle";
}, function (_ref3) {
  var removeMarkers = _ref3.removeMarkers;
  return removeMarkers ? "none" : "square";
});
/**
 * @deprecated Please do not use this component in new code. It will be removed in a future release.
 */
var BulletedList = function BulletedList(_ref4) {
  var children = _ref4.children,
    _ref4$removeMarkers = _ref4.removeMarkers,
    removeMarkers = _ref4$removeMarkers === void 0 ? false : _ref4$removeMarkers;
  return /*#__PURE__*/React.createElement(StyledUnorderedList, {
    role: "list",
    removeMarkers: removeMarkers
  }, children);
};

var _excluded$m = ["children"];
var _templateObject$C;
var StyledItem = styled.li(_templateObject$C || (_templateObject$C = _taggedTemplateLiteral(["\n  flex-shrink: 0;\n  flex-grow: 0;\n"])));

// eslint-disable-next-line react/display-name
var CarouselItem = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded$m);
  return /*#__PURE__*/React.createElement(StyledItem, _extends({
    ref: ref,
    "data-testid": "lp-components-carousel-slide"
  }, rest), children);
});

/* istanbul ignore file */
var clamp = function clamp(_ref) {
  var min = _ref.min,
    max = _ref.max,
    value = _ref.value;
  return Math.min(Math.max(value, min), max);
};
var a11yScrollTo = function a11yScrollTo(scrollAreaRef, left, scrollBehavior) {
  var _scrollAreaRef$curren;
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (typeof (scrollAreaRef === null || scrollAreaRef === void 0 || (_scrollAreaRef$curren = scrollAreaRef.current) === null || _scrollAreaRef$curren === void 0 ? void 0 : _scrollAreaRef$curren.scrollTo) !== 'function') return;
  scrollAreaRef.current.scrollTo({
    left: left,
    behavior: prefersReducedMotion.matches ? 'auto' : scrollBehavior
  });
};
var calculateMaxScrollLeft = function calculateMaxScrollLeft(scrollArea) {
  return scrollArea.scrollWidth - scrollArea.clientWidth;
};
var calculateUpcomingItemIndex = function calculateUpcomingItemIndex(_ref2) {
  var scrollArea = _ref2.scrollArea,
    scrollLeft = _ref2.scrollLeft,
    direction = _ref2.direction;
  return function (item) {
    var itemRightBounds = item.offsetLeft + item.offsetWidth;
    var remainingRightScrollWidth = scrollArea.offsetWidth + scrollLeft;
    var remainingLeftScrollWidth = scrollLeft - scrollArea.offsetWidth;
    return direction === 'next' ? itemRightBounds > remainingRightScrollWidth : itemRightBounds > remainingLeftScrollWidth;
  };
};
var createCarouselStore = function createCarouselStore() {
  return createStore()(function (set, get) {
    return {
      scrollAreaRef: null,
      itemsRef: null,
      scrollLeft: 0,
      maxScrollLeft: 0,
      scrollBy: 'viewport',
      currentIndex: 0,
      init: function init(_ref3) {
        var _itemsRef$current, _scrollAreaRef$curren2;
        var scrollAreaRef = _ref3.scrollAreaRef,
          itemsRef = _ref3.itemsRef,
          onScroll = _ref3.onScroll,
          scrollBy = _ref3.scrollBy;
        if (!scrollAreaRef.current || !(itemsRef !== null && itemsRef !== void 0 && (_itemsRef$current = itemsRef.current) !== null && _itemsRef$current !== void 0 && _itemsRef$current.length)) return function () {};
        var maxScrollLeft = calculateMaxScrollLeft(scrollAreaRef.current);
        set({
          scrollAreaRef: scrollAreaRef,
          maxScrollLeft: maxScrollLeft,
          itemsRef: itemsRef,
          scrollBy: scrollBy
        });
        var scrollHandler = function scrollHandler(evt) {
          var _itemsRef$current$fin, _itemsRef$current2;
          var scrollLeft = Math.round(evt.target.scrollLeft);
          var currentIndex = (_itemsRef$current$fin = itemsRef === null || itemsRef === void 0 || (_itemsRef$current2 = itemsRef.current) === null || _itemsRef$current2 === void 0 ? void 0 : _itemsRef$current2.findIndex(function (item) {
            return scrollLeft < item.offsetLeft + item.offsetWidth;
          })) !== null && _itemsRef$current$fin !== void 0 ? _itemsRef$current$fin : 0;
          if (onScroll) onScroll({
            scrollLeft: scrollLeft,
            currentIndex: currentIndex
          });
          set({
            scrollLeft: scrollLeft,
            currentIndex: currentIndex
          });
        };
        (_scrollAreaRef$curren2 = scrollAreaRef.current) === null || _scrollAreaRef$curren2 === void 0 || _scrollAreaRef$curren2.addEventListener('scroll', scrollHandler);
        var resetState = function resetState() {
          var _scrollAreaRef$curren3;
          (_scrollAreaRef$curren3 = scrollAreaRef.current) === null || _scrollAreaRef$curren3 === void 0 || _scrollAreaRef$curren3.removeEventListener('scroll', scrollHandler);
          set({
            scrollAreaRef: null,
            itemsRef: null,
            scrollLeft: 0,
            maxScrollLeft: 0,
            currentIndex: 0
          });
        };
        if ('ResizeObserver' in window) {
          var resizeObserver = new ResizeObserver(function (entries) {
            window.requestAnimationFrame(function () {
              var _itemsRef$current3;
              if (!Array.isArray(entries) || !entries.length || !scrollAreaRef.current || !(itemsRef !== null && itemsRef !== void 0 && (_itemsRef$current3 = itemsRef.current) !== null && _itemsRef$current3 !== void 0 && _itemsRef$current3.length)) return;
              set({
                maxScrollLeft: calculateMaxScrollLeft(scrollAreaRef.current)
              });
            });
          });
          resizeObserver.observe(scrollAreaRef === null || scrollAreaRef === void 0 ? void 0 : scrollAreaRef.current);
          return function () {
            resizeObserver.disconnect();
            resetState();
          };
        }
        set({
          maxScrollLeft: calculateMaxScrollLeft(scrollAreaRef.current)
        });
        return function () {
          resetState();
        };
      },
      slideByNumberItems: function slideByNumberItems(amount, direction, scrollBehavior) {
        var _itemsRef$current4;
        var _get = get(),
          itemsRef = _get.itemsRef,
          scrollAreaRef = _get.scrollAreaRef,
          currentIndex = _get.currentIndex;
        if (!(scrollAreaRef !== null && scrollAreaRef !== void 0 && scrollAreaRef.current) || !(itemsRef !== null && itemsRef !== void 0 && (_itemsRef$current4 = itemsRef.current) !== null && _itemsRef$current4 !== void 0 && _itemsRef$current4.length)) return;
        var upcomingItemIndex = clamp({
          min: 0,
          max: itemsRef.current.length - 1,
          value: direction === 'next' ? currentIndex + amount : currentIndex - amount
        });
        a11yScrollTo(scrollAreaRef, itemsRef.current[upcomingItemIndex].offsetLeft, scrollBehavior);
      },
      slideByViewport: function slideByViewport(direction, scrollBehavior) {
        var _itemsRef$current5;
        var _get2 = get(),
          itemsRef = _get2.itemsRef,
          scrollAreaRef = _get2.scrollAreaRef,
          scrollLeft = _get2.scrollLeft,
          currentIndex = _get2.currentIndex;
        if (!(scrollAreaRef !== null && scrollAreaRef !== void 0 && scrollAreaRef.current) || !(itemsRef !== null && itemsRef !== void 0 && (_itemsRef$current5 = itemsRef.current) !== null && _itemsRef$current5 !== void 0 && _itemsRef$current5.length)) return;
        var scrollArea = scrollAreaRef.current;
        var upcomingItemIndex = itemsRef.current.findIndex(calculateUpcomingItemIndex({
          scrollLeft: scrollLeft,
          direction: direction,
          scrollArea: scrollArea
        }));
        if (upcomingItemIndex === -1) return;
        var currentItemLargerThanVisibleArea = upcomingItemIndex === currentIndex;
        if (direction === 'next' && currentItemLargerThanVisibleArea) upcomingItemIndex += 1;
        if (direction === 'previous' && currentItemLargerThanVisibleArea) upcomingItemIndex -= 1;
        upcomingItemIndex = clamp({
          min: 0,
          max: itemsRef.current.length - 1,
          value: upcomingItemIndex
        });
        a11yScrollTo(scrollAreaRef, itemsRef.current[upcomingItemIndex].offsetLeft, scrollBehavior);
      },
      slideTo: function slideTo(direction) {
        var _itemsRef$current6;
        var scrollBehavior = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'smooth';
        var _get3 = get(),
          itemsRef = _get3.itemsRef,
          scrollAreaRef = _get3.scrollAreaRef,
          scrollBy = _get3.scrollBy,
          slideByViewport = _get3.slideByViewport,
          slideByNumberItems = _get3.slideByNumberItems;
        if (!(scrollAreaRef !== null && scrollAreaRef !== void 0 && scrollAreaRef.current) || !(itemsRef !== null && itemsRef !== void 0 && (_itemsRef$current6 = itemsRef.current) !== null && _itemsRef$current6 !== void 0 && _itemsRef$current6.length)) return;
        if (scrollBy === 'viewport') slideByViewport(direction, scrollBehavior);
        if (scrollBy !== 'viewport') slideByNumberItems(scrollBy, direction, scrollBehavior);
      },
      gotoItemIndex: function gotoItemIndex(index) {
        var _itemsRef$current7;
        var scrollBehavior = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'smooth';
        var _get4 = get(),
          scrollAreaRef = _get4.scrollAreaRef,
          itemsRef = _get4.itemsRef;
        if (!(scrollAreaRef !== null && scrollAreaRef !== void 0 && scrollAreaRef.current) || !(itemsRef !== null && itemsRef !== void 0 && (_itemsRef$current7 = itemsRef.current) !== null && _itemsRef$current7 !== void 0 && _itemsRef$current7.length)) return;
        var upcomingItemIndex = clamp({
          min: 0,
          max: itemsRef.current.length - 1,
          value: index
        });
        a11yScrollTo(scrollAreaRef, itemsRef.current[upcomingItemIndex].offsetLeft, scrollBehavior);
      },
      gotoNextPage: function gotoNextPage() {
        var _itemsRef$current8, _itemsRef$current9;
        var _get5 = get(),
          scrollAreaRef = _get5.scrollAreaRef,
          itemsRef = _get5.itemsRef,
          slideTo = _get5.slideTo,
          maxScrollLeft = _get5.maxScrollLeft;
        if (!(scrollAreaRef !== null && scrollAreaRef !== void 0 && scrollAreaRef.current) || !(itemsRef !== null && itemsRef !== void 0 && (_itemsRef$current8 = itemsRef.current) !== null && _itemsRef$current8 !== void 0 && _itemsRef$current8.length)) return;
        if ((itemsRef === null || itemsRef === void 0 || (_itemsRef$current9 = itemsRef.current) === null || _itemsRef$current9 === void 0 ? void 0 : _itemsRef$current9.length) === 1) {
          a11yScrollTo(scrollAreaRef, maxScrollLeft, 'smooth');
          return;
        }
        slideTo('next');
      },
      gotoPreviousPage: function gotoPreviousPage() {
        var _itemsRef$current0, _itemsRef$current1;
        var _get6 = get(),
          scrollAreaRef = _get6.scrollAreaRef,
          itemsRef = _get6.itemsRef,
          slideTo = _get6.slideTo;
        if (!(scrollAreaRef !== null && scrollAreaRef !== void 0 && scrollAreaRef.current) || !(itemsRef !== null && itemsRef !== void 0 && (_itemsRef$current0 = itemsRef.current) !== null && _itemsRef$current0 !== void 0 && _itemsRef$current0.length)) return;
        if ((itemsRef === null || itemsRef === void 0 || (_itemsRef$current1 = itemsRef.current) === null || _itemsRef$current1 === void 0 ? void 0 : _itemsRef$current1.length) === 1) {
          a11yScrollTo(scrollAreaRef, 0, 'smooth');
          return;
        }
        slideTo('previous');
      }
    };
  });
};
var storeContext = /*#__PURE__*/createContext(null);
var CarouselState = function CarouselState(_ref4) {
  var children = _ref4.children;
  var store = useRef(createCarouselStore()).current;
  return /*#__PURE__*/React.createElement(storeContext.Provider, {
    value: store
  }, children);
};
var useCarousel = function useCarousel(selector, equalityFn) {
  var store = useContext(storeContext);
  if (!store) throw new Error('Missing CarouselState in the tree');
  return useStoreWithEqualityFn(store, selector, equalityFn);
};
var carouselShallowCompare = shallow;

var _excluded$l = ["children", "onClick"],
  _excluded2$1 = ["children", "onClick"];
var _templateObject$B, _templateObject2$m, _templateObject3$a, _templateObject4$4, _templateObject5$4;
var UnstyledButton = styled.button(_templateObject$B || (_templateObject$B = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  width: 44px;\n  height: 44px;\n"])));
var UnstyledCarouselPreviousButton = function UnstyledCarouselPreviousButton(_ref) {
  var children = _ref.children,
    _onClick = _ref.onClick,
    rest = _objectWithoutProperties(_ref, _excluded$l);
  var t = useLocaleText();
  var _useCarousel = useCarousel(function (state) {
      return {
        scrollLeft: state.scrollLeft,
        gotoPreviousPage: state.gotoPreviousPage
      };
    }, shallow),
    scrollLeft = _useCarousel.scrollLeft,
    gotoPreviousPage = _useCarousel.gotoPreviousPage;
  return /*#__PURE__*/React.createElement(UnstyledButton, _extends({
    disabled: scrollLeft === 0,
    type: "button",
    "aria-label": t('carousel.previous_slide'),
    onClick: function onClick(evt) {
      gotoPreviousPage();
      if (_onClick) _onClick(evt);
    },
    tabIndex: -1
  }, rest), children);
};
var UnstyledCarouselNextButton = function UnstyledCarouselNextButton(_ref2) {
  var children = _ref2.children,
    _onClick2 = _ref2.onClick,
    rest = _objectWithoutProperties(_ref2, _excluded2$1);
  var t = useLocaleText();
  var _useCarousel2 = useCarousel(function (state) {
      return {
        maxScrollLeft: state.maxScrollLeft,
        scrollLeft: state.scrollLeft,
        gotoNextPage: state.gotoNextPage
      };
    }, shallow),
    maxScrollLeft = _useCarousel2.maxScrollLeft,
    scrollLeft = _useCarousel2.scrollLeft,
    gotoNextPage = _useCarousel2.gotoNextPage;
  return /*#__PURE__*/React.createElement(UnstyledButton, _extends({
    disabled: scrollLeft === maxScrollLeft,
    type: "button",
    "aria-label": t('carousel.next_slide'),
    onClick: function onClick(evt) {
      gotoNextPage();
      if (_onClick2) _onClick2(evt);
    },
    tabIndex: -1
  }, rest), children);
};
var sharedButtonStyles = css(_templateObject2$m || (_templateObject2$m = _taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  margin: auto 0;\n  z-index: 1;\n  -webkit-tap-highlight-color: transparent;\n\n  &:disabled {\n    visibility: hidden;\n  }\n"])));
var StyledButtonContent = styled.div(_templateObject3$a || (_templateObject3$a = _taggedTemplateLiteral(["\n  width: 32px;\n  aspect-ratio: 1;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n\n  ", "\n"])), system({
  color: 'colorTextHeadline',
  bgcolor: 'colorBackground01',
  borderRadius: 7,
  boxShadow: 4
}));
var StyledCarouselPreviousButton = styled(UnstyledCarouselPreviousButton)(_templateObject4$4 || (_templateObject4$4 = _taggedTemplateLiteral(["\n  left: 0;\n  ", "\n"])), sharedButtonStyles);
var CarouselPreviousButton = function CarouselPreviousButton(props) {
  return /*#__PURE__*/React.createElement(StyledCarouselPreviousButton, props, /*#__PURE__*/React.createElement(StyledButtonContent, null, /*#__PURE__*/React.createElement(IconSystemArrowBack, {
    width: '1rem'
  })));
};
var StyledCarouselNextButton = styled(UnstyledCarouselNextButton)(_templateObject5$4 || (_templateObject5$4 = _taggedTemplateLiteral(["\n  right: 0;\n  ", ";\n"])), sharedButtonStyles);
var CarouselNextButton = function CarouselNextButton(props) {
  return /*#__PURE__*/React.createElement(StyledCarouselNextButton, props, /*#__PURE__*/React.createElement(StyledButtonContent, null, /*#__PURE__*/React.createElement(IconSystemArrowForward, {
    width: '1rem'
  })));
};

var _excluded$k = ["children", "href"];
var TextLink = function TextLink(_ref) {
  var children = _ref.children,
    href = _ref.href,
    rest = _objectWithoutProperties(_ref, _excluded$k);
  return /*#__PURE__*/React.createElement(Link, _extends({
    href: href
  }, rest), children);
};

var _excluded$j = ["skipLinkHref", "children"];
var _templateObject$A, _templateObject2$l;
var StyledSkipLink = styled(TextLink)(_templateObject$A || (_templateObject$A = _taggedTemplateLiteral(["\n  opacity: 0;\n  position: absolute;\n  z-index: 10;\n  ", "\n  &:focus {\n    opacity: 1;\n  }\n"])), system({
  bgcolor: 'colorBackground01'
}));
var UnstyledCarouselRoot = function UnstyledCarouselRoot(_ref) {
  var skipLinkHref = _ref.skipLinkHref,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded$j);
  var t = useLocaleText();
  var ariaLabel = rest['aria-label'];
  return /*#__PURE__*/React.createElement("div", _extends({
    role: "region",
    "aria-roledescription": t('carousel.description'),
    "data-testid": "lp-components-carousel"
  }, rest), skipLinkHref && /*#__PURE__*/React.createElement(StyledSkipLink, {
    href: skipLinkHref
  }, ariaLabel ? t('carousel.skip_link.with_label', ariaLabel) : t('carousel.skip_link.default')), children);
};
var CarouselRoot = styled(UnstyledCarouselRoot)(_templateObject2$l || (_templateObject2$l = _taggedTemplateLiteral(["\n  position: relative;\n  ", "\n"])), system({
  px: 6
}));

var useInView = function useInView(elementRef) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    inView = _useState2[0],
    setInView = _useState2[1];
  useEffect(function () {
    if (!elementRef.current) return function () {};
    var element = elementRef.current;
    var intersectionObserver = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(element);
        }
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 1
    });
    intersectionObserver.observe(element);
    return function () {
      intersectionObserver.disconnect();
    };
  }, [elementRef]);
  return inView;
};

var _excluded$i = ["children", "onScroll", "animateInView", "scrollBy"];
var _templateObject$z, _templateObject2$k;
var BOUNCE_INTENSITY = 30;
var DefaultStyledScrollArea = styled.ul(_templateObject$z || (_templateObject$z = _taggedTemplateLiteral(["\n  ", "\n  display: flex;\n  overflow-x: scroll;\n  list-style-type: none;\n\n  animation-duration: 1000ms;\n  animation-fill-mode: both;\n\n  @media (prefers-reduced-motion: reduce) {\n    animation: none;\n  }\n\n  @keyframes bounce {\n    0%,\n    20%,\n    50%,\n    80%,\n    100% {\n      transform: translateX(0);\n    }\n    40% {\n      transform: translateX(-", "px);\n    }\n    60% {\n      transform: translateX(-", "px);\n    }\n  }\n\n  -webkit-overflow-scrolling: touch;\n  scrollbar-width: none;\n\n  &::-webkit-scrollbar {\n    display: none;\n  }\n"])), system({
  m: 0,
  px: 0,
  py: 0
}), BOUNCE_INTENSITY, BOUNCE_INTENSITY * 0.5);
var UnstyledCarouselScrollArea = function UnstyledCarouselScrollArea(_ref) {
  var children = _ref.children,
    onScroll = _ref.onScroll,
    animateInView = _ref.animateInView,
    _ref$scrollBy = _ref.scrollBy,
    scrollBy = _ref$scrollBy === void 0 ? 'viewport' : _ref$scrollBy,
    rest = _objectWithoutProperties(_ref, _excluded$i);
  var childrenAsArray = React.Children.toArray(children);
  var lengthOfItems = useRef(childrenAsArray.length);
  var itemsRef = useRef([]);
  var scrollAreaRef = useRef(null);
  var init = useCarousel(function (state) {
    return state.init;
  });
  var gotoIndex = useCarousel(function (state) {
    return state.gotoItemIndex;
  });
  var maxScrollLeft = useCarousel(function (state) {
    return state.maxScrollLeft;
  });
  var inView = useInView(scrollAreaRef);
  useEffect(function () {
    var unregister = init({
      scrollAreaRef: scrollAreaRef,
      itemsRef: itemsRef,
      onScroll: onScroll,
      scrollBy: scrollBy
    });

    /* Tested by the interaction tests in Playwright */
    /* istanbul ignore next */
    if (childrenAsArray.length !== lengthOfItems.current) {
      gotoIndex(0, 'auto');
      lengthOfItems.current = childrenAsArray.length;
    }
    return unregister;
  }, [init, onScroll, childrenAsArray.length, gotoIndex, scrollBy]);

  // Position relative is needed for the scrollLeft to be calculated from the left of the
  // carousel, rather than the left of the nearest element with position relative.
  return /*#__PURE__*/React.createElement("div", {
    style: {
      overflow: 'hidden',
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(DefaultStyledScrollArea, _extends({
    ref: scrollAreaRef,
    "data-testid": "lp-carousel-scroll-area",
    style: {
      animationName: /* istanbul ignore next : covered by interaction tests */animateInView && inView && maxScrollLeft > 0 ? 'bounce' : ''
    }
  }, rest), childrenAsArray.map(function (child, index) {
    return /*#__PURE__*/React.cloneElement(child, {
      ref: function ref(el) {
        if (el) {
          itemsRef.current[index] = el;
        }
      },
      'data-testid': "lp-carousel-item-".concat(index)
    });
  })));
};
var CarouselScrollArea = styled(UnstyledCarouselScrollArea)(_templateObject2$k || (_templateObject2$k = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  gap: 24
}));

var _excluded$h = ["children"];
var UnstyledCarouselControls = function UnstyledCarouselControls(_ref) {
  var children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded$h);
  var t = useLocaleText();
  return /*#__PURE__*/React.createElement("div", _extends({
    role: 'group',
    "aria-label": t('carousel.controls')
  }, rest), children);
};
var CarouselControls = UnstyledCarouselControls;

var _excluded$g = ["scrollBy", "onScroll", "animateInView", "centerNonScrollableItems", "gap", "onButtonClick", "skipLinkHref", "children"],
  _excluded2 = ["children"];
var _templateObject$y, _templateObject2$j, _templateObject3$9;
var SIMPLE_CAROUSEL_BUTTON_CONTAINER_OFFSET = '-6px';
var StyledCarouselRoot = styled(UnstyledCarouselRoot)(_templateObject$y || (_templateObject$y = _taggedTemplateLiteral(["\n  position: relative;\n\n  ", "\n"])), system({
  px: {
    md: 6
  }
}));
var StyledCarouselScrollArea = styled(UnstyledCarouselScrollArea)(_templateObject2$j || (_templateObject2$j = _taggedTemplateLiteral(["\n  ", "\n  /* Material UI does not support system typing scale */\n  gap: ", ";\n"])), function (_ref) {
  var theme = _ref.theme,
    centerItems = _ref.centerItems;
  return system({
    justifyContent: centerItems ? {
      md: 'center'
    } : 'initial'
  })({
    theme: theme
  });
}, function (_ref2) {
  var theme = _ref2.theme,
    gap = _ref2.gap;
  return spacingPx(gap)({
    theme: theme
  });
});
var StyledCarouselControls = styled(UnstyledCarouselControls).attrs(function (_ref3) {
  var hidden = _ref3.hidden;
  return {
    'aria-hidden': hidden ? 'true' : undefined
  };
})(_templateObject3$9 || (_templateObject3$9 = _taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  left: ", ";\n  right: ", ";\n  ", "\n"])), SIMPLE_CAROUSEL_BUTTON_CONTAINER_OFFSET, SIMPLE_CAROUSEL_BUTTON_CONTAINER_OFFSET, system({
  display: {
    xs: 'none',
    md: 'block'
  }
}));
var CarouselDOMWrapper = function CarouselDOMWrapper(props) {
  var _useCarousel = useCarousel(function (state) {
      return {
        scrollLeft: state.scrollLeft,
        maxScrollLeft: state.maxScrollLeft
      };
    }),
    scrollLeft = _useCarousel.scrollLeft,
    maxScrollLeft = _useCarousel.maxScrollLeft;
  var scrollBy = props.scrollBy,
    onScroll = props.onScroll,
    animateInView = props.animateInView,
    _props$centerNonScrol = props.centerNonScrollableItems,
    centerNonScrollableItems = _props$centerNonScrol === void 0 ? false : _props$centerNonScrol,
    _props$gap = props.gap,
    gap = _props$gap === void 0 ? 8 : _props$gap,
    onButtonClick = props.onButtonClick,
    skipLinkHref = props.skipLinkHref,
    children = props.children,
    ariaLabels = _objectWithoutProperties(props, _excluded$g);
  var BREAKPOINT = 640;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    controlsHidden = _useState2[0],
    setControlsHidden = _useState2[1];
  var childrenAsArray = React.Children.toArray(children);
  var itemsCentered = centerNonScrollableItems && scrollLeft === 0 && maxScrollLeft === scrollLeft;
  var buildButtonHandler = function buildButtonHandler(direction) {
    return function (evt) {
      if (onButtonClick) onButtonClick(direction, evt);
    };
  };
  useEffect(function () {
    var handleResize = function handleResize() {
      setControlsHidden(window.innerWidth < BREAKPOINT);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return function () {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return /*#__PURE__*/React.createElement(StyledCarouselRoot, _extends({
    skipLinkHref: skipLinkHref
  }, ariaLabels), /*#__PURE__*/React.createElement(StyledCarouselControls, {
    hidden: controlsHidden
  }, /*#__PURE__*/React.createElement(CarouselPreviousButton, {
    onClick: buildButtonHandler('previous')
  }), /*#__PURE__*/React.createElement(CarouselNextButton, {
    onClick: buildButtonHandler('next')
  })), /*#__PURE__*/React.createElement(StyledCarouselScrollArea, {
    gap: gap,
    centerItems: itemsCentered,
    scrollBy: scrollBy,
    onScroll: onScroll,
    animateInView: animateInView
  }, childrenAsArray.map(function (child, index) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line react/no-array-index-key
      React.createElement(CarouselItem, {
        key: "lp-carousel-item-".concat(index)
      }, child)
    );
  })));
};
var SimpleCarousel = function SimpleCarousel(_ref4) {
  var children = _ref4.children,
    rest = _objectWithoutProperties(_ref4, _excluded2);
  return /*#__PURE__*/React.createElement(CarouselState, null, /*#__PURE__*/React.createElement(CarouselDOMWrapper, rest, children));
};

var _templateObject$x;
var chipStyles = css(_templateObject$x || (_templateObject$x = _taggedTemplateLiteral(["\n  transition: background-color 150ms linear, box-shadow 150ms linear;\n  cursor: pointer;\n  white-space: nowrap;\n  box-sizing: border-box;\n  display: flex;\n  justify-content: center;\n  min-width: 70px;\n  flex-shrink: 0;\n\n  ", "\n\n  &[aria-selected='true'] {\n    ", "\n  }\n\n  &:not(:disabled) {\n    &:hover {\n      text-decoration: none;\n      outline: none;\n\n      ", "\n\n      &[aria-selected='true'] {\n        ", "\n      }\n\n      .dismiss-icon {\n        ", "\n      }\n    }\n\n    &:focus-visible {\n      text-decoration: none;\n      outline: none;\n\n      ", "\n\n      &[aria-selected='true'] {\n        ", "\n      }\n\n      .dismiss-icon {\n        ", "\n      }\n    }\n  }\n"])), function (_ref) {
  var selected = _ref.selected;
  return system({
    p: selected ? 4 : '10px',
    border: selected ? 4 : 2,
    borderColor: selected ? 'colorInteractionSelectedState' : 'colorTextHeadline',
    bgcolor: selected ? 'colorBackground03' : 'colorBlack00',
    color: selected ? 'colorInteractionSelectedState' : 'colorTextHeadline',
    borderRadius: 6,
    typography: 'typeBodyLabel'
  });
}, system({
  p: 4,
  border: 4,
  borderColor: 'colorInteractionSelectedState',
  bgcolor: 'colorBackground03',
  color: 'colorInteractionSelectedState'
}), function (_ref2) {
  var selected = _ref2.selected;
  return system({
    p: selected ? 4 : '9px',
    border: selected ? 4 : 3,
    borderColor: 'colorInteractionHoverState',
    bgcolor: 'colorBackground06',
    color: 'colorInteractionHoverState'
  });
}, system({
  p: 4,
  border: 4
}), system({
  bgcolor: 'colorInteractionHoverState',
  color: 'colorBackground06'
}), function (_ref3) {
  var selected = _ref3.selected;
  return system({
    p: selected ? 4 : '9px',
    border: selected ? 4 : 3,
    borderColor: 'colorInteractionHoverState',
    bgcolor: 'colorBackground06',
    color: 'colorInteractionHoverState'
  });
}, system({
  p: 4,
  border: 4
}), system({
  bgcolor: 'colorInteractionHoverState',
  color: 'colorBackground06'
}));

var _excluded$f = ["children", "dismissible", "selected"];
var _templateObject$w, _templateObject2$i, _templateObject3$8;
var ICON_SIZE$1 = spacingPx(8);
var StyledIconCross = styled(IconSystemCrossSmall)(_templateObject$w || (_templateObject$w = _taggedTemplateLiteral(["\n  width: ", ";\n  height: ", ";\n  ", "\n"])), ICON_SIZE$1, ICON_SIZE$1, system({
  ml: 4,
  borderRadius: 7,
  bgcolor: 'colorInteractionSelectedState',
  color: 'colorBackground03'
}));
var StyledChip = styled.button(_templateObject2$i || (_templateObject2$i = _taggedTemplateLiteral(["\n  ", "\n"])), chipStyles);
var StyledSpan = styled.span(_templateObject3$8 || (_templateObject3$8 = _taggedTemplateLiteral(["\n  display: flex;\n  text-align: center;\n"])));
var Chip = function Chip(_ref) {
  var children = _ref.children,
    dismissible = _ref.dismissible,
    selected = _ref.selected,
    rest = _objectWithoutProperties(_ref, _excluded$f);
  var showDismissButton = selected && dismissible;
  return /*#__PURE__*/React.createElement(StyledChip, _extends({
    selected: selected
  }, rest), /*#__PURE__*/React.createElement(StyledSpan, null, children, showDismissButton && /*#__PURE__*/React.createElement(StyledIconCross, {
    className: "dismiss-icon"
  })));
};

var _excluded$e = ["to", "linkAs", "children"];
var CustomLink = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var to = _ref.to,
    _ref$linkAs = _ref.linkAs,
    LinkAs = _ref$linkAs === void 0 ? 'a' : _ref$linkAs,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded$e);
  return LinkAs === 'a' ? /*#__PURE__*/React.createElement("a", _extends({
    href: to
  }, rest, {
    ref: ref
  }), children) : /*#__PURE__*/React.createElement(LinkAs, _extends({
    to: to
  }, rest, {
    ref: ref
  }), children);
});
CustomLink.displayName = 'CustomLink';

var _excluded$d = ["decorative", "withMargin"];
var _templateObject$v;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var StyledDivider = styled.hr(_templateObject$v || (_templateObject$v = _taggedTemplateLiteral(["\n  width: 100%;\n  height: 1px;\n  ", "\n"])), function (_ref) {
  var withMargin = _ref.withMargin;
  return system(_objectSpread({
    bgcolor: 'colorBlack20',
    border: 0,
    borderRadius: 6
  }, withMargin && {
    marginTop: 12,
    marginBottom: 10
  }));
});
var Divider = function Divider(_ref2) {
  var decorative = _ref2.decorative,
    _ref2$withMargin = _ref2.withMargin,
    withMargin = _ref2$withMargin === void 0 ? true : _ref2$withMargin,
    rest = _objectWithoutProperties(_ref2, _excluded$d);
  return /*#__PURE__*/React.createElement(StyledDivider, _extends({
    withMargin: withMargin
  }, decorative && {
    as: 'div'
  }, rest));
};

var Emoji = function Emoji(_ref) {
  var symbol = _ref.symbol,
    label = _ref.label;
  return /*#__PURE__*/React.createElement("span", {
    role: "img",
    "aria-label": label || '',
    "aria-hidden": label ? 'false' : 'true'
  }, symbol);
};

var _templateObject$u, _templateObject2$h;
// See: https://github.com/styled-components/jest-styled-components/issues/232
/* istanbul ignore next */
var GlobalStyle = createGlobalStyle(_templateObject$u || (_templateObject$u = _taggedTemplateLiteral(["\n  html {\n    -webkit-font-smoothing: auto;\n    -moz-osx-font-smoothing: auto;\n    box-sizing: border-box;\n  }\n\n  *, *::before, *::after  {\n    box-sizing: inherit;\n  }\n\n  body {\n    margin: 0;\n    ", "\n  }\n\n  strong {\n    font-weight: 700;\n  }\n\n  a {\n    ", "\n    text-decoration: none;\n\n    &:hover {\n      text-decoration: underline;\n    }\n  }\n\n  ol, ul {\n    list-style: none;\n    margin: 0;\n    padding: 0;\n    text-indent: 0;\n  }\n\n  h1 {\n    ", "\n  }\n\n  h2 {\n    ", "\n  }\n\n  h3 {\n    ", "\n  }\n\n  h1, h2, h3, h4, h5, p {\n    margin: 0 0 1rem 0;\n  }\n\n  button {\n    border: 0;\n    border-radius: 0;\n    margin: 0;\n    padding: 0;\n    background: transparent;\n\n    &:not(:disabled) {\n      cursor: pointer;\n    }\n  }\n\n  ", "\n"])), system({
  bgcolor: 'colorBackgroundSite',
  color: 'colorTextBody',
  typography: 'typeBodyText'
}), system({
  color: 'colorInteractionTextLink'
}), system({
  typography: 'typeDisplay01'
}), system({
  typography: 'typeDisplay02'
}), system({
  typography: 'typeDisplay03'
}), function (_ref) {
  var theme = _ref.theme,
    env = _ref.env;
  return createFontFaceStyles(theme.fontFaces, env);
});

// See: https://github.com/styled-components/jest-styled-components/issues/232
/* istanbul ignore next */
var GlobalStyleWithTokens = createGlobalStyle(_templateObject2$h || (_templateObject2$h = _taggedTemplateLiteral(["\n  html {\n    -webkit-font-smoothing: auto;\n    -moz-osx-font-smoothing: auto;\n    box-sizing: border-box;\n    overflow-y: scroll;\n    ", "\n  }\n\n  *, *::before, *::after  {\n    box-sizing: inherit;\n  }\n\n  body {\n    margin: 0;\n    ", "\n  }\n\n  strong {\n    font-weight: 700;\n  }\n\n  a {\n    ", "\n    text-decoration: none;\n\n    &:hover {\n      text-decoration: underline;\n    }\n  }\n\n  ol, ul {\n    list-style: none;\n    margin: 0;\n    padding: 0;\n    text-indent: 0;\n  }\n\n  h1 {\n    ", "\n  }\n\n  h2 {\n    ", "\n  }\n\n  h3 {\n    ", "\n  }\n\n  h1, h2, h3, h4, h5, p {\n    margin: 0 0 1rem 0;\n  }\n\n  button {\n    border: 0;\n    border-radius: 0;\n    margin: 0;\n    padding: 0;\n    background: transparent;\n\n    &:not(:disabled) {\n      cursor: pointer;\n    }\n  }\n\n  ", "\n"])), system({
  typography: 'typeBodyText'
}), system({
  bgcolor: 'colorBackgroundSite',
  color: 'colorTextBody'
}), system({
  color: 'colorInteractionTextLink'
}), system({
  typography: 'typeDisplay01'
}), system({
  typography: 'typeDisplay02'
}), system({
  typography: 'typeDisplay03'
}), function (_ref2) {
  var theme = _ref2.theme,
    env = _ref2.env;
  return createFontFaceStyles(theme.fontFaces, env);
});

var _templateObject$t;
var defaultGap = {
  xs: 6,
  lg: 8
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var neg = function neg(gap) {
  if (typeof gap === 'number') return -gap;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.keys(gap).reduce(function (acc, curr) {
    acc[curr] = -gap[curr];
    return acc;
  }, {});
};
var getColGap = function getColGap(_ref) {
  var gap = _ref.gap,
    colGap = _ref.colGap;
  var v = colGap || gap || 0;
  if (v && typeof v === 'boolean') {
    v = defaultGap;
  }
  return v;
};
var getRowGap = function getRowGap(_ref2) {
  var gap = _ref2.gap,
    rowGap = _ref2.rowGap;
  var v = rowGap || gap || 0;
  if (v && typeof v === 'boolean') {
    v = defaultGap;
  }
  return v;
};
var outerGap = function outerGap(props) {
  var colGap = getColGap(props);
  var rowGap = getRowGap(props);
  return system({
    ml: neg(colGap),
    mb: neg(rowGap)
  });
};
var innerGap = function innerGap(props) {
  var colGap = getColGap(props);
  var rowGap = getRowGap(props);
  return system({
    pl: colGap,
    pb: rowGap
  });
};
var Grid = styled.div(_templateObject$t || (_templateObject$t = _taggedTemplateLiteral(["\n  display: flex;\n  flex-wrap: wrap;\n  ", ";\n  ", ";\n  ", "\n  > * {\n    ", "\n  }\n"])), flexbox, spacing, outerGap, innerGap);

var _excluded$c = ["children", "level", "typography"];

// Omits color as it clashes with MUI's pallete

var levelMap = {
  h1: 'typeDisplay01',
  h2: 'typeDisplay02',
  h3: 'typeDisplay03',
  h4: 'typeDisplay04',
  h5: 'typeDisplay05',
  h6: 'typeDisplay06'
};
var Heading = function Heading(_ref) {
  var children = _ref.children,
    level = _ref.level,
    typography = _ref.typography,
    rest = _objectWithoutProperties(_ref, _excluded$c);
  return /*#__PURE__*/React.createElement(Text, _extends({
    color: "colorTextHeadline",
    typography: !typography ? levelMap[level] : typography,
    as: level
  }, rest), children);
};

var _templateObject$s;
var animationStyles = css(_templateObject$s || (_templateObject$s = _taggedTemplateLiteral(["\n  --spring-easing: linear(\n    0,\n    0.0114 0.7%,\n    0.044,\n    0.095 2.1%,\n    0.176 3%,\n    0.347 4.4%,\n    0.889 8.3%,\n    1.121 10.4%,\n    1.2699 12%,\n    1.309 13.5%,\n    1.333 15.3%,\n    1.326 16.2%,\n    1.285 17.8%,\n    1.218 19.5%,\n    1.037 23.5%,\n    0.955 25.7%,\n    0.91 28%,\n    0.89 29.7%,\n    0.8937 31.8%,\n    0.921 34.3%,\n    0.982 38.2%,\n    1.007 40.2%,\n    1.033 44.7%,\n    1.033 47.8%,\n    0.998 55.4%,\n    0.988 62.9%,\n    1.001 70.4%,\n    1.004 74.5%,\n    1 89%,\n    1 99.96%\n  );\n  --spring-duration: 1.2s;\n"])));

var _templateObject$r, _templateObject2$g, _templateObject3$7;
var ICON_BORDER_WIDTH = '2px';
var TOGGLE_SIZE = 40;
var StyledButton$2 = styled.button(_templateObject$r || (_templateObject$r = _taggedTemplateLiteral(["\n  ", "\n\n  ", "\n"])), system({
  padding: 3,
  borderRadius: 2
}), focusIndicatorStyles);
var StyledWrapper = styled.span(_templateObject2$g || (_templateObject2$g = _taggedTemplateLiteral(["\n  ", "\n\n  &:hover,\n  &:active {\n    ", "\n  }\n\n  &.is-toggled {\n    &:hover,\n    &:active {\n      ", "\n    }\n  }\n"])), system({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: "".concat(TOGGLE_SIZE, "px"),
  height: "".concat(TOGGLE_SIZE, "px"),
  border: 1,
  borderColor: 'colorBorder03',
  borderRadius: "".concat(TOGGLE_SIZE, "px"),
  boxShadow: 2,
  bgcolor: 'colorBackground01'
}), system({
  bgcolor: 'colorInteractionControlHoverFill'
}), system({
  bgcolor: 'colorBackgroundError'
}));
var StyledHeart = styled.span(_templateObject3$7 || (_templateObject3$7 = _taggedTemplateLiteral(["\n  ", "\n\n  ", "\n\n  svg {\n    transition: transform var(--spring-duration) var(--spring-easing);\n    transform: scale(1);\n  }\n\n  path {\n    stroke-width: ", ";\n    stroke: ", ";\n    fill-opacity: 0;\n    transition: stroke 100ms ease-in-out, fill 100ms ease-in-out,\n      fill-opacity 100ms ease-in-out;\n  }\n\n  &.is-toggled path {\n    stroke: ", ";\n    fill: ", ";\n    fill-opacity: 1;\n  }\n\n  &.is-pressed svg {\n    transform: scale(0.7);\n  }\n"])), system({
  width: 'iconMedium',
  height: 'iconMedium'
}), animationStyles, ICON_BORDER_WIDTH, colorValue('colorInteractionIcon'), colorValue('colorInteractionIconFavourite'), colorValue('colorInteractionIconFavourite'));
var HeartToggle = function HeartToggle(_ref) {
  var initialToggledState = _ref.isToggled,
    onToggle = _ref.onToggle,
    ariaLabel = _ref.ariaLabel;
  var _useState = useState(initialToggledState),
    _useState2 = _slicedToArray(_useState, 2),
    isToggled = _useState2[0],
    setIsToggled = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isPressed = _useState4[0],
    setIsPressed = _useState4[1];
  var reduceMotion = useReducedMotion();
  var handleClick = function handleClick(e) {
    setIsToggled(!isToggled);
    onToggle(e);
  };
  var handleMouseDown = function handleMouseDown() {
    return setIsPressed(true);
  };
  var handleMouseUp = function handleMouseUp() {
    return setIsPressed(false);
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') setIsPressed(true);
  };
  var handleKeyUp = function handleKeyUp(e) {
    if (e.key === 'Enter' || e.key === ' ') setIsPressed(false);
  };
  var handleTouchStart = function handleTouchStart() {
    return setIsPressed(true);
  };
  var handleTouchEnd = function handleTouchEnd() {
    return setIsPressed(false);
  };
  var buildClassName = function buildClassName() {
    var className = '';
    if (isToggled) className += 'is-toggled ';
    if (!reduceMotion) {
      if (isPressed) className += 'is-pressed';
    }
    return className;
  };
  return /*#__PURE__*/React.createElement(StyledButton$2, {
    onClick: handleClick,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    "aria-label": ariaLabel,
    "aria-pressed": isToggled
  }, /*#__PURE__*/React.createElement(StyledWrapper, {
    className: "".concat(isToggled ? 'is-toggled' : '')
  }, /*#__PURE__*/React.createElement(StyledHeart, {
    "data-testid": "lp-components-heart-toggle-icon-wrapper",
    className: buildClassName()
  }, /*#__PURE__*/React.createElement(IconSystemHeart, {
    "aria-hidden": true
  }))));
};

var _templateObject$q;
var VerticalStack = styled(Box)(_templateObject$q || (_templateObject$q = _taggedTemplateLiteral(["\n  > * + * {\n    margin-top: ", ";\n  }\n"])), function (_ref) {
  var spacing = _ref.spacing;
  return spacingPx(spacing);
});

var _templateObject$p;
var StyledList$1 = styled.ul(_templateObject$p || (_templateObject$p = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: ", ";\n  gap: ", ";\n"])), function (_ref) {
  var direction = _ref.direction;
  return direction === 'horizontal' ? 'row' : 'column';
}, spacingPx(5));
/**
 * @deprecated Please do not use this component in new code. It will be removed in a future release.
 */
var IconList = function IconList(_ref2) {
  var children = _ref2.children,
    _ref2$direction = _ref2.direction,
    direction = _ref2$direction === void 0 ? 'vertical' : _ref2$direction,
    listHeading = _ref2.listHeading,
    listHeadingLevel = _ref2.listHeadingLevel;
  return /*#__PURE__*/React.createElement(VerticalStack, {
    spacing: 5
  }, listHeading && /*#__PURE__*/React.createElement(Heading, {
    level: listHeadingLevel || 'h4'
  }, listHeading), /*#__PURE__*/React.createElement(StyledList$1, {
    direction: direction,
    role: "list"
  }, children));
};

var _templateObject$o, _templateObject2$f;
var ICON_SIZE = '1.4rem';
var StyledLi = styled.li(_templateObject$o || (_templateObject$o = _taggedTemplateLiteral(["\n  list-style-type: none;\n"])));
var StyledFlex = styled(Flex)(_templateObject2$f || (_templateObject2$f = _taggedTemplateLiteral(["\n  gap: ", ";\n  align-items: start;\n"])), spacingPx(4));
/**
 * @deprecated Please do not use this component in new code. It will be removed in a future release.
 */
var IconListItem = function IconListItem(_ref) {
  var children = _ref.children,
    icon = _ref.icon,
    iconColor = _ref.iconColor,
    heading = _ref.heading;
  var ItemIcon = function ItemIcon() {
    return /*#__PURE__*/React.cloneElement(icon, {
      style: {
        width: ICON_SIZE,
        height: ICON_SIZE
      },
      color: colorValue(iconColor || 'colorTextHeadline')({
        theme: theme
      }),
      role: 'presentation',
      'data-testid': 'lp-components-iconlist-item-icon'
    });
  };
  if (heading) {
    return /*#__PURE__*/React.createElement(StyledLi, null, /*#__PURE__*/React.createElement(StyledFlex, null, /*#__PURE__*/React.createElement(ItemIcon, null), /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column"
    }, /*#__PURE__*/React.createElement(Text, {
      typography: "typeBodyLabel",
      color: "colorTextHeadline",
      as: heading.level,
      m: 0
    }, heading.text), /*#__PURE__*/React.createElement(Text, {
      typography: "typeBodyText",
      color: "colorTextBody"
    }, children))));
  }
  return /*#__PURE__*/React.createElement(StyledLi, null, /*#__PURE__*/React.createElement(StyledFlex, null, /*#__PURE__*/React.createElement(ItemIcon, null), /*#__PURE__*/React.createElement(Text, {
    typography: "typeBodyText",
    color: "colorTextBody"
  }, children)));
};

var _excluded$b = ["aspectRatio", "alt", "crossOrigin", "decoding", "height", "sizes", "src", "srcSet", "zoomSrc", "useMap", "width", "data-testid", "aria-hidden", "itemProp", "className", "imageOnLoadCallback", "lazyLoad", "loading"];
var _templateObject$n;
var StyledFigure = styled.figure(_templateObject$n || (_templateObject$n = _taggedTemplateLiteral(["\n  img {\n    transition: opacity 300ms ease;\n  }\n  &:hover {\n    cursor: ", ";\n    img {\n      opacity: ", ";\n    }\n  }\n\n  @media (hover: none) {\n    pointer-events: none;\n  }\n"])), function (_ref) {
  var imgZoom = _ref.imgZoom;
  return imgZoom ? 'zoom-out' : 'zoom-in';
}, function (_ref2) {
  var imgZoom = _ref2.imgZoom;
  return imgZoom ? '0' : '1';
});
var Image = function Image(_ref3) {
  var aspectRatio = _ref3.aspectRatio,
    alt = _ref3.alt,
    crossOrigin = _ref3.crossOrigin,
    decoding = _ref3.decoding,
    height = _ref3.height,
    sizes = _ref3.sizes,
    src = _ref3.src,
    srcSet = _ref3.srcSet,
    zoomSrc = _ref3.zoomSrc,
    useMap = _ref3.useMap,
    width = _ref3.width,
    testId = _ref3['data-testid'],
    ariaHidden = _ref3['aria-hidden'],
    itemProp = _ref3.itemProp,
    className = _ref3.className,
    imageOnLoadCallback = _ref3.imageOnLoadCallback,
    lazyLoad = _ref3.lazyLoad,
    _ref3$loading = _ref3.loading,
    loading = _ref3$loading === void 0 ? lazyLoad ? 'lazy' : undefined : _ref3$loading,
    props = _objectWithoutProperties(_ref3, _excluded$b);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    imgZoom = _useState2[0],
    setImgZoom = _useState2[1];
  var _useState3 = useState('0% 0%'),
    _useState4 = _slicedToArray(_useState3, 2),
    bgPos = _useState4[0],
    setBgPos = _useState4[1];
  var imageSource = typeof src === 'string' ? src : src["default"];
  var srcSetSource = _typeof(src) === 'object' ? src.webp : '';
  var imgProps = {
    loading: loading,
    alt: alt,
    crossOrigin: crossOrigin,
    decoding: decoding,
    height: height,
    sizes: sizes,
    useMap: useMap,
    width: width,
    'data-testid': testId,
    'aria-hidden': ariaHidden,
    itemProp: itemProp,
    className: className,
    onLoad: imageOnLoadCallback,
    src: imageSource,
    srcSet: srcSet
  };
  var mouseMove = function mouseMove(e) {
    e.persist();
    var _getBoundingClientRec = e.target.getBoundingClientRect(),
      left = _getBoundingClientRec.left,
      top = _getBoundingClientRec.top,
      rectWidth = _getBoundingClientRec.width,
      rectHeight = _getBoundingClientRec.height;
    var x = (e.pageX - left) / rectWidth * 100;
    var y = (e.clientY - top) / rectHeight * 100;
    setBgPos("".concat(x, "% ").concat(y, "%"));
  };
  var imgElement = zoomSrc ? /*#__PURE__*/React.createElement(StyledFigure, {
    style: {
      margin: 0,
      backgroundImage: imgZoom ? "url(\"".concat(zoomSrc, "\")") : 'none',
      backgroundPosition: bgPos
    },
    "data-testid": testId && "".concat(testId, "-figure"),
    onClick: function onClick() {
      return setImgZoom(!imgZoom);
    },
    imgZoom: imgZoom,
    onMouseMove: function onMouseMove(e) {
      return mouseMove(e);
    },
    onMouseOut: function onMouseOut() {
      return setImgZoom(false);
    }
  }, /*#__PURE__*/React.createElement("img", _extends({}, imgProps, {
    className: "".concat(imgZoom ? 'imgZoom' : 'noImgZoom', " ").concat(imgProps.className || ''),
    alt: imgProps.alt || ''
  }))) : /*#__PURE__*/React.createElement("img", _extends({}, imgProps, {
    alt: imgProps.alt || ''
  }));
  var toRender = typeof src !== 'string' ? /*#__PURE__*/React.createElement("picture", null, /*#__PURE__*/React.createElement("source", {
    type: "image/webp",
    "data-testid": 'lp-image-webp',
    srcSet: srcSetSource
  }), imgElement) : imgElement;
  return aspectRatio !== undefined ? /*#__PURE__*/React.createElement(AspectRatio, _extends({
    ratio: aspectRatio
  }, props), toRender) : toRender;
};

var _excluded$a = ["backgroundImageUrl", "iconImageUrl", "icon", "leftAlignContent", "fullWidth", "children"];
var _templateObject$m, _templateObject2$e, _templateObject3$6;
var StyledInfoStrip = styled.div(_templateObject$m || (_templateObject$m = _taggedTemplateLiteral(["\n  ", "\n  ", "\n  ", "\n"])), function (_ref) {
  var imgUrl = _ref.imgUrl;
  return imgUrl && "\n    background-image: url(".concat(imgUrl, ");\n    background-position: 0% 50%;\n    background-size: cover;\n  ");
}, sizingSpacing, function (_ref2) {
  var imgUrl = _ref2.imgUrl,
    leftAlignContent = _ref2.leftAlignContent;
  return system({
    py: imgUrl ? 6 : 2,
    textAlign: leftAlignContent ? 'left' : 'center'
  });
});
var StyledContent = styled.div(_templateObject2$e || (_templateObject2$e = _taggedTemplateLiteral(["\n  display: inline-flex;\n  align-items: center;\n  ", "\n"])), function (_ref3) {
  var hasBackground = _ref3.hasBackground,
    fullWidth = _ref3.fullWidth;
  return system({
    py: 4,
    borderRadius: 1,
    boxShadow: hasBackground ? 3 : 0,
    bgcolor: hasBackground ? 'colorBackground01' : 'colorBackground05',
    typography: {
      xs: 'typeMobileDisplay05',
      md: 'typeDesktopDisplay06'
    },
    px: fullWidth ? 6 : 0,
    width: fullWidth ? '100%' : 'auto'
  });
});
var StyledIconImage = styled.div(_templateObject3$6 || (_templateObject3$6 = _taggedTemplateLiteral(["\n  flex-shrink: 0;\n  ", "\n"])), system({
  ml: 4,
  mr: -4,
  width: '40px',
  height: '40px'
}));
var InfoStrip = function InfoStrip(_ref4) {
  var backgroundImageUrl = _ref4.backgroundImageUrl,
    iconImageUrl = _ref4.iconImageUrl,
    icon = _ref4.icon,
    _ref4$leftAlignConten = _ref4.leftAlignContent,
    leftAlignContent = _ref4$leftAlignConten === void 0 ? false : _ref4$leftAlignConten,
    _ref4$fullWidth = _ref4.fullWidth,
    fullWidth = _ref4$fullWidth === void 0 ? false : _ref4$fullWidth,
    children = _ref4.children,
    props = _objectWithoutProperties(_ref4, _excluded$a);
  return /*#__PURE__*/React.createElement(StyledInfoStrip, _extends({
    imgUrl: backgroundImageUrl,
    leftAlignContent: leftAlignContent
  }, props), /*#__PURE__*/React.createElement(Container, {
    limitWidth: !fullWidth,
    padding: fullWidth ? 0 : undefined
  }, /*#__PURE__*/React.createElement(StyledContent, {
    "data-testid": "lp-components-info-strip-content",
    hasBackground: !!backgroundImageUrl,
    fullWidth: fullWidth
  }, icon && /*#__PURE__*/React.createElement(StyledIconImage, {
    "aria-hidden": true
  }, /*#__PURE__*/React.cloneElement(icon, {
    width: 40,
    height: 40,
    'data-testid': 'lp-components-info-strip-icon'
  })), iconImageUrl && !icon && /*#__PURE__*/React.createElement(StyledIconImage, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Image, {
    aspectRatio: 1,
    "data-testid": "lp-components-info-strip-icon",
    src: iconImageUrl
  })), children)));
};

var _templateObject$l;
var StyledScreenReaderOnly = styled.span(_templateObject$l || (_templateObject$l = _taggedTemplateLiteral(["\n  ", "\n"])), screenReaderOnly);
var ScreenReaderOnly = function ScreenReaderOnly(_ref) {
  var children = _ref.children,
    testId = _ref.testId,
    as = _ref.as,
    htmlFor = _ref.htmlFor,
    id = _ref.id;
  var props = {
    htmlFor: htmlFor,
    id: id,
    as: as
  };
  return /*#__PURE__*/React.createElement(StyledScreenReaderOnly, _extends({
    "data-testid": testId
  }, props), children);
};

var _templateObject$k, _templateObject2$d, _templateObject3$5, _templateObject4$3, _templateObject5$3;
var MODAL_FROM_BREAKPOINT$1 = 'md';
var CLOSE_BUTTON_HEIGHT$1 = 56;
var BANNER_HEIGHT = 70;
var PAGE_PEEK_HEIGHT = 100;
var DEFAULT_BACKGROUND_COLOUR$1 = 'colorBackground01';
var CONTENT_SPACING_FACTOR = 0.3;

/* --- OVERLAY --- */

var StyledOverlay$1 = styled.div(_templateObject$k || (_templateObject$k = _taggedTemplateLiteral(["\n  z-index: 2000;\n  ", ";\n\n  position: fixed;\n  right: 0;\n  left: 0;\n  bottom: 0;\n  top: 0;\n  display: flex;\n"])), function (_ref) {
  var mobilePagePeek = _ref.mobilePagePeek,
    theme = _ref.theme;
  return system({
    bgcolor: mobilePagePeek ? 'rgba(0, 0, 0, 0.6)' : _defineProperty({
      xs: colorValue('colorBackground01')({
        theme: theme
      })
    }, MODAL_FROM_BREAKPOINT$1, 'rgba(0, 0, 0, 0.6)')
  });
});

/* --- BANNER --- */

var StyledModalBanner = styled.div(_templateObject2$d || (_templateObject2$d = _taggedTemplateLiteral(["\n  ", "\n\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  max-height: ", "px;\n  min-height: ", "px;\n"])), function (_ref3) {
  var bgColour = _ref3.bgColour,
    theme = _ref3.theme;
  return system({
    bgcolor: bgColour ? colorValue(bgColour)({
      theme: theme
    }) : colorValue(DEFAULT_BACKGROUND_COLOUR$1)({
      theme: theme
    }),
    p: 3
  });
}, BANNER_HEIGHT, CLOSE_BUTTON_HEIGHT$1);

/* --- MODAL --- */

var StyledModalContent$1 = styled.div(_templateObject3$5 || (_templateObject3$5 = _taggedTemplateLiteral(["\n  ", ";\n  -webkit-overflow-scrolling: touch;\n"])), function (_ref4) {
  var mobilePagePeek = _ref4.mobilePagePeek,
    viewportHeight = _ref4.viewportHeight,
    bgColour = _ref4.bgColour,
    theme = _ref4.theme;
  return system({
    alignSelf: mobilePagePeek && _defineProperty({
      xs: 'flex-end'
    }, MODAL_FROM_BREAKPOINT$1, 'center'),
    bgcolor: bgColour ? colorValue(bgColour)({
      theme: theme
    }) : colorValue(DEFAULT_BACKGROUND_COLOUR$1)({
      theme: theme
    }),
    maxHeight: mobilePagePeek && viewportHeight + PAGE_PEEK_HEIGHT,
    mx: 'auto',
    mt: mobilePagePeek ? 'auto' : _defineProperty({}, MODAL_FROM_BREAKPOINT$1, 'auto'),
    mb: mobilePagePeek ? _defineProperty({}, MODAL_FROM_BREAKPOINT$1, 'auto') : 'auto'
  });
});

/* --- CLOSE BUTTON --- */

var StyledCloseButton$1 = styled.div(_templateObject4$3 || (_templateObject4$3 = _taggedTemplateLiteral(["\n  ", "\n\n  display: flex;\n  flex-direction: row-reverse;\n  height: ", "px;\n"])), function (_ref8) {
  var bgColour = _ref8.bgColour;
  return system({
    bgcolor: bgColour ? colorValue(bgColour) : colorValue(DEFAULT_BACKGROUND_COLOUR$1),
    p: 3
  });
}, CLOSE_BUTTON_HEIGHT$1);

/* --- CONTENT --- */

var calculateHeightOffset = function calculateHeightOffset(hasCloseButton, mobilePagePeek) {
  return [hasCloseButton ? CLOSE_BUTTON_HEIGHT$1 : 0, mobilePagePeek ? PAGE_PEEK_HEIGHT : 0].reduce(function (acc, height) {
    return acc + height;
  }, 0);
};
var StyledModalBody$1 = styled(Box)(_templateObject5$3 || (_templateObject5$3 = _taggedTemplateLiteral(["\n  ", ";\n\n  max-height: ", "px;\n\n  max-width: 100vw;\n  ", " {\n    max-width: calc(100vw - ", "px);\n  }\n  overflow-y: auto;\n  outline: none;\n  margin: auto;\n\n  ", "\n\n  > * {\n    max-width: 100%;\n  }\n"])), function (_ref9) {
  var hasCloseButton = _ref9.hasCloseButton;
  return system({
    pt: hasCloseButton ? 0 : _defineProperty({
      xs: 6
    }, MODAL_FROM_BREAKPOINT$1, 10)
  });
}, function (_ref1) {
  var viewportHeight = _ref1.viewportHeight;
  return viewportHeight;
}, breakpoint(MODAL_FROM_BREAKPOINT$1), function (_ref10) {
  var theme = _ref10.theme;
  return theme.spacing[10];
}, system({
  px: _defineProperty({
    xs: 5
  }, MODAL_FROM_BREAKPOINT$1, 10),
  pb: _defineProperty({
    xs: 5
  }, MODAL_FROM_BREAKPOINT$1, 10)
}));
var Modal = function Modal(_ref11) {
  var label = _ref11.label,
    description = _ref11.description,
    _ref11$isOpen = _ref11.isOpen,
    isOpen = _ref11$isOpen === void 0 ? false : _ref11$isOpen,
    hasCloseButton = _ref11.hasCloseButton,
    onDismiss = _ref11.onDismiss,
    _ref11$testId = _ref11.testId,
    testId = _ref11$testId === void 0 ? 'lp-components-modal' : _ref11$testId,
    children = _ref11.children,
    closeButtonLabel = _ref11.closeButtonLabel,
    mobilePagePeek = _ref11.mobilePagePeek,
    bannerContent = _ref11.bannerContent,
    triggerRef = _ref11.triggerRef,
    colours = _ref11.colours,
    addContentSpacing = _ref11.addContentSpacing;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    mounted = _useState2[0],
    setMounted = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    viewportHeight = _useState4[0],
    setViewportHeight = _useState4[1];
  var heightOffset = useMemo(function () {
    return calculateHeightOffset(hasCloseButton, mobilePagePeek);
  }, [hasCloseButton, mobilePagePeek]);
  var isOpenMemo = useRef(false);
  var t = useLocaleText();
  var getExtraContentSpacing = useCallback(/* istanbul ignore next */function () {
    var spacing = heightOffset + (addContentSpacing ? window.innerHeight * CONTENT_SPACING_FACTOR : 0);
    return spacing;
  }, [addContentSpacing, heightOffset]);
  var handleWindowResize = useCallback(/* istanbul ignore next */function () {
    return debounce(/* istanbul ignore next */function (e) {
      if (window.visualViewport && window.visualViewport.height < viewportHeight) {
        /* instanbul ignore next */setViewportHeight(e.target.height + getExtraContentSpacing());
      }
    }, 150);
  }, [viewportHeight, getExtraContentSpacing]);
  useEffect(function () {
    if (viewportHeight === 0) {
      setViewportHeight(window.innerHeight - heightOffset);
    }
  }, [viewportHeight, heightOffset]);
  useEffect(function () {
    var _window$visualViewpor;
    window.addEventListener('resize', handleWindowResize);
    (_window$visualViewpor = window.visualViewport) === null || _window$visualViewpor === void 0 || _window$visualViewpor.addEventListener('resize', handleWindowResize);
    return function () {
      var _window$visualViewpor2;
      window.removeEventListener('resize', handleWindowResize);
      (_window$visualViewpor2 = window.visualViewport) === null || _window$visualViewpor2 === void 0 || _window$visualViewpor2.removeEventListener('resize', handleWindowResize);
    };
  }, [handleWindowResize]);
  var closeModal = useCallback(function () {
    setViewportHeight(0);
    if (onDismiss) onDismiss();
  }, [onDismiss]);
  useEffect(function () {
    if (isOpen) {
      isOpenMemo.current = true;
    }
    if (!isOpen && isOpenMemo.current && triggerRef !== null && triggerRef !== void 0 && triggerRef.current) {
      isOpenMemo.current = false;
      triggerRef.current.focus();
    }
  }, [isOpen, triggerRef]);

  // Render after mounting to avoid SSR issues
  useEffect(function () {
    setMounted(true);
  }, []);
  if (!mounted) return null;
  return /*#__PURE__*/React.createElement(Dialog.Root, {
    open: isOpen
  }, /*#__PURE__*/React.createElement(Dialog.Portal, null, /*#__PURE__*/React.createElement(Dialog.Overlay, {
    "data-testid": "".concat(testId, "-overlay")
  }), /*#__PURE__*/React.createElement(StyledOverlay$1, {
    mobilePagePeek: mobilePagePeek,
    "data-testid": "".concat(testId, "-overlay-styles")
  }, /*#__PURE__*/React.createElement(Dialog.Content, {
    asChild: true,
    onEscapeKeyDown: closeModal,
    onPointerDownOutside: closeModal
  }, /*#__PURE__*/React.createElement(StyledModalContent$1, {
    mobilePagePeek: mobilePagePeek,
    viewportHeight: viewportHeight,
    bgColour: colours === null || colours === void 0 ? void 0 : colours.background,
    "data-testid": "".concat(testId),
    "aria-modal": isOpen,
    "aria-label": label
  }, /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement(Dialog.Title, null, label)), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement(Dialog.Description, null, description || t('modal.description'))), bannerContent ? /*#__PURE__*/React.createElement(StyledModalBanner, {
    bgColour: (colours === null || colours === void 0 ? void 0 : colours.bannerBackground) || (colours === null || colours === void 0 ? void 0 : colours.background)
  }, bannerContent, hasCloseButton && /*#__PURE__*/React.createElement(IconButton, {
    icon: IconSystemCross,
    label: closeButtonLabel || t('common.close'),
    onClick: closeModal,
    colours: colours === null || colours === void 0 ? void 0 : colours.closeButton
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, hasCloseButton && /*#__PURE__*/React.createElement(StyledCloseButton$1, {
    bgColour: (colours === null || colours === void 0 ? void 0 : colours.bannerBackground) || (colours === null || colours === void 0 ? void 0 : colours.background)
  }, /*#__PURE__*/React.createElement(IconButton, {
    icon: IconSystemCross,
    label: closeButtonLabel || t('common.close'),
    onClick: closeModal,
    colours: colours === null || colours === void 0 ? void 0 : colours.closeButton
  }))), /*#__PURE__*/React.createElement(StyledModalBody$1, {
    hasCloseButton: !!hasCloseButton,
    "data-testid": "".concat(testId, "-body"),
    viewportHeight: viewportHeight
  }, children))))));
};

var _templateObject$j;
var StyledOverlay = styled.div(_templateObject$j || (_templateObject$j = _taggedTemplateLiteral(["\n  z-index: 2000;\n  ", "\n  position: fixed;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  max-height: 100vh;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n"])), system({
  bgcolor: {
    xs: 'colorBackground01',
    md: 'rgba(0, 0, 0, 0.6)'
  }
}));
var ModalOverlay = function ModalOverlay(_ref) {
  var isOpen = _ref.isOpen,
    triggerRef = _ref.triggerRef,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? 'lp-components-modal' : _ref$testId,
    children = _ref.children;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    mounted = _useState2[0],
    setMounted = _useState2[1];
  var firstUpdate = useRef(true);
  useEffect(function () {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (!isOpen && triggerRef !== null && triggerRef !== void 0 && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [isOpen, triggerRef]);

  // Render after mounting to avoid SSR issues
  useEffect(function () {
    setMounted(true);
  }, []);
  if (!mounted) return null;
  return /*#__PURE__*/React.createElement(Dialog.Root, {
    open: isOpen
  }, /*#__PURE__*/React.createElement(Dialog.Portal, null, /*#__PURE__*/React.createElement(Dialog.Overlay, {
    asChild: true
  }, /*#__PURE__*/React.createElement(StyledOverlay, {
    "data-testid": "".concat(testId, "-overlay")
  }, children))));
};

var _excluded$9 = ["testId", "bgColor", "children"];
var _templateObject$i;
var StyledModalBody = styled(Box)(_templateObject$i || (_templateObject$i = _taggedTemplateLiteral(["\n  outline: none;\n  flex: 1;\n  overflow: auto;\n  ", ";\n"])), function (_ref) {
  var bgColor = _ref.bgColor,
    theme = _ref.theme;
  return system({
    bgcolor: bgColor ? colorValue(bgColor)({
      theme: theme
    }) : 'inherit'
  });
});
var ModalBody = function ModalBody(_ref2) {
  var _ref2$testId = _ref2.testId,
    testId = _ref2$testId === void 0 ? 'lp-components-modal' : _ref2$testId,
    bgColor = _ref2.bgColor,
    children = _ref2.children,
    rest = _objectWithoutProperties(_ref2, _excluded$9);
  return /*#__PURE__*/React.createElement(StyledModalBody, _extends({
    "data-testid": "".concat(testId, "-body"),
    bgColor: bgColor
  }, rest), children);
};

var _excluded$8 = ["hasCloseButton", "onDismiss", "testId", "closeButtonLabel", "colours", "children"];
var _templateObject$h, _templateObject2$c;
var CLOSE_BUTTON_HEIGHT = 56;
var StyledCloseButton = styled.div(_templateObject$h || (_templateObject$h = _taggedTemplateLiteral(["\n  ", "\n\n  position: absolute;\n  right: 0;\n  top: 0;\n  height: ", "px;\n"])), function (_ref) {
  var bgColor = _ref.bgColor,
    theme = _ref.theme;
  return system({
    bgcolor: bgColor ? colorValue(bgColor)({
      theme: theme
    }) : 'inherit',
    p: 3
  });
}, CLOSE_BUTTON_HEIGHT);
var StyledHeader = styled.div(_templateObject2$c || (_templateObject2$c = _taggedTemplateLiteral(["\n  position: relative;\n  ", "\n"])), function (_ref2) {
  var bgColor = _ref2.bgColor,
    theme = _ref2.theme,
    hasCloseButton = _ref2.hasCloseButton;
  return system({
    bgcolor: bgColor ? colorValue(bgColor)({
      theme: theme
    }) : 'inherit',
    minHeight: hasCloseButton ? CLOSE_BUTTON_HEIGHT : 'auto'
  });
});
var ModalHeader = function ModalHeader(_ref3) {
  var hasCloseButton = _ref3.hasCloseButton,
    onDismiss = _ref3.onDismiss,
    _ref3$testId = _ref3.testId,
    testId = _ref3$testId === void 0 ? 'lp-components-modal-header' : _ref3$testId,
    _ref3$closeButtonLabe = _ref3.closeButtonLabel,
    closeButtonLabel = _ref3$closeButtonLabe === void 0 ? 'Close' : _ref3$closeButtonLabe,
    colours = _ref3.colours,
    children = _ref3.children,
    rest = _objectWithoutProperties(_ref3, _excluded$8);
  var closeModal = useCallback(function () {
    if (onDismiss) onDismiss();
  }, [onDismiss]);
  return /*#__PURE__*/React.createElement(StyledHeader, _extends({
    "data-testid": testId,
    bgColor: colours === null || colours === void 0 ? void 0 : colours.background,
    hasCloseButton: hasCloseButton
  }, rest), children, hasCloseButton && /*#__PURE__*/React.createElement(StyledCloseButton, {
    bgColor: (colours === null || colours === void 0 ? void 0 : colours.bannerBackground) || (colours === null || colours === void 0 ? void 0 : colours.background),
    "data-testid": "".concat(testId, "-close-button")
  }, /*#__PURE__*/React.createElement(IconButton, {
    icon: IconSystemCross,
    label: closeButtonLabel,
    onClick: closeModal,
    colours: colours === null || colours === void 0 ? void 0 : colours.closeButton
  })));
};

var _excluded$7 = ["label", "description", "bgColor", "testId", "children", "onDismiss", "disablePointerDownOutside"];
var _templateObject$g;
var DEFAULT_BACKGROUND_COLOUR = 'colorBackground01';
var MODAL_FROM_BREAKPOINT = 'md';
var StyledModalContent = styled.div(_templateObject$g || (_templateObject$g = _taggedTemplateLiteral(["\n  max-height: 100%;\n  max-width: 100vw;\n  margin: auto;\n\n  display: flex;\n  flex-direction: column;\n\n  ", " {\n    max-width: calc(100vw - ", "px);\n  }\n  ", ";\n  -webkit-overflow-scrolling: touch;\n\n  ", "\n"])), breakpoint(MODAL_FROM_BREAKPOINT), function (_ref) {
  var theme = _ref.theme;
  return theme.spacing[10];
}, function (_ref2) {
  var bgColor = _ref2.bgColor;
  return system({
    bgcolor: bgColor || DEFAULT_BACKGROUND_COLOUR,
    m: 'auto'
  });
}, sizingSpacing);
var ModalContent = function ModalContent(_ref3) {
  var label = _ref3.label,
    description = _ref3.description,
    bgColor = _ref3.bgColor,
    _ref3$testId = _ref3.testId,
    testId = _ref3$testId === void 0 ? 'lp-components-modal' : _ref3$testId,
    children = _ref3.children,
    onDismiss = _ref3.onDismiss,
    _ref3$disablePointerD = _ref3.disablePointerDownOutside,
    disablePointerDownOutside = _ref3$disablePointerD === void 0 ? false : _ref3$disablePointerD,
    rest = _objectWithoutProperties(_ref3, _excluded$7);
  var t = useLocaleText();
  var closeModal = useCallback(function () {
    if (onDismiss) onDismiss();
  }, [onDismiss]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  var handlePointerDownOutside = function handlePointerDownOutside(e) {
    if (disablePointerDownOutside) {
      e.preventDefault();
    } else {
      closeModal();
    }
  };
  return /*#__PURE__*/React.createElement(Dialog.Content, {
    asChild: true,
    onEscapeKeyDown: closeModal,
    onPointerDownOutside: function onPointerDownOutside(e) {
      return handlePointerDownOutside(e);
    },
    "data-testid": testId,
    "aria-label": label,
    "aria-modal": "true"
  }, /*#__PURE__*/React.createElement(StyledModalContent, _extends({
    bgColor: bgColor
  }, rest), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement(Dialog.Title, null, label)), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement(Dialog.Description, null, description || t('modal.description'))), children));
};

var _templateObject$f;
var StyledOrderedList = styled.ol(_templateObject$f || (_templateObject$f = _taggedTemplateLiteral(["\n  ", "\n\n  ", "\n\n  & > li > ol {\n    list-style-type: ", ";\n\n    & > li > ol {\n      list-style-type: ", ";\n    }\n  }\n"])), commonStyles, function (_ref) {
  var removeMarkers = _ref.removeMarkers;
  return !removeMarkers && "list-style-type: decimal;";
}, function (_ref2) {
  var removeMarkers = _ref2.removeMarkers;
  return removeMarkers ? "none" : "lower-alpha";
}, function (_ref3) {
  var removeMarkers = _ref3.removeMarkers;
  return removeMarkers ? "none" : "upper-roman";
});
var NumberedList = function NumberedList(_ref4) {
  var children = _ref4.children,
    _ref4$removeMarkers = _ref4.removeMarkers,
    removeMarkers = _ref4$removeMarkers === void 0 ? false : _ref4$removeMarkers;
  return /*#__PURE__*/React.createElement(StyledOrderedList, {
    role: "list",
    removeMarkers: removeMarkers
  }, children);
};

var _templateObject$e, _templateObject2$b;
var MENU_VERTICAL_POSITION = '60px';
var MENU_HORIZONTAL_POSITION = '0px';
var StyledContainer$1 = styled.div(_templateObject$e || (_templateObject$e = _taggedTemplateLiteral(["\n  position: relative;\n  overflow: visible;\n"])));
var StyledMenu = styled.ul(_templateObject2$b || (_templateObject2$b = _taggedTemplateLiteral(["\n  position: absolute;\n  top: ", ";\n  right: ", ";\n  z-index: 1;\n  white-space: nowrap;\n  ", ";\n\n  &.right {\n    left: ", ";\n    right: auto;\n  }\n\n  &.above {\n    bottom: ", ";\n    top: auto;\n  }\n\n  li {\n    list-style-type: none;\n  }\n"])), MENU_VERTICAL_POSITION, MENU_HORIZONTAL_POSITION, system({
  p: 6,
  bgcolor: 'colorBackground01',
  borderRadius: 2,
  boxShadow: 2
}), MENU_HORIZONTAL_POSITION, MENU_VERTICAL_POSITION);
var OptionsMenu = function OptionsMenu(_ref) {
  var _ref$horizontalAlign = _ref.horizontalAlign,
    horizontalAlign = _ref$horizontalAlign === void 0 ? 'left' : _ref$horizontalAlign,
    _ref$verticalAlign = _ref.verticalAlign,
    verticalAlign = _ref$verticalAlign === void 0 ? 'below' : _ref$verticalAlign,
    children = _ref.children,
    label = _ref.label;
  var containerElement = useRef(null);
  var buttonElement = useRef(null);
  var menuElement = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    optionsOpen = _useState2[0],
    setOptionsOpen = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    focusedIndex = _useState4[0],
    setFocusedIndex = _useState4[1];
  var toggleOptionsMenu = function toggleOptionsMenu() {
    setOptionsOpen(!optionsOpen);
    setFocusedIndex(0);
  };
  useEffect(function () {
    var handleClickOutside = function handleClickOutside(event) {
      if (!optionsOpen) return;
      if (containerElement.current && !containerElement.current.contains(event.target)) {
        toggleOptionsMenu();
      }
    };
    var handleKeyDown = function handleKeyDown(event) {
      // istanbul ignore next
      if (!optionsOpen || !menuElement.current || !buttonElement.current) return;
      if (event.key === 'Escape') {
        setOptionsOpen(false);
        buttonElement.current.focus();
      }
      var menuItems = menuElement.current.querySelectorAll('li');
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setFocusedIndex(function (prevIndex) {
          var newIndex = (prevIndex + 1) % menuItems.length;
          menuItems[newIndex].focus();
          return newIndex;
        });
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setFocusedIndex(function (prevIndex) {
          var newIndex = (prevIndex - 1 + menuItems.length) % menuItems.length;
          menuItems[newIndex].focus();
          return newIndex;
        });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  });
  useEffect(function () {
    if (optionsOpen && menuElement.current && focusedIndex !== null) {
      var menuItems = menuElement.current.querySelectorAll('li');
      var menuItem = menuItems[focusedIndex];
      var focusableChild = menuItem.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
      if (focusableChild) {
        focusableChild.focus();
      } else {
        menuItem.focus();
      }
    }
  }, [optionsOpen, focusedIndex]);
  var classes = "".concat(horizontalAlign, " ").concat(verticalAlign);
  return /*#__PURE__*/React.createElement(StyledContainer$1, {
    ref: containerElement
  }, /*#__PURE__*/React.createElement(IconButton, {
    ref: buttonElement,
    "aria-haspopup": "menu",
    onClick: toggleOptionsMenu,
    label: label,
    icon: IconSystemMenuOptions
  }), optionsOpen && /*#__PURE__*/React.createElement(StyledMenu, {
    role: "menu",
    "aria-labelledby": label,
    ref: menuElement,
    tabIndex: -1,
    className: classes,
    onClick: toggleOptionsMenu
  }, React.Children.map(children, function (child, index) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line react/no-array-index-key
      React.createElement("li", {
        key: index,
        role: "menuitem",
        tabIndex: -1
      }, child)
    );
  })));
};

var _templateObject$d;
var OUR_PICK_ICON_SIZE = 22;
var StyledPill$1 = styled(Text)(_templateObject$d || (_templateObject$d = _taggedTemplateLiteral(["\n  padding: 4px 0;\n  gap: 6px;\n  justify-content: center;\n  ", "\n  display: inline-flex;\n  min-width: 135px;\n"])), system({
  bgcolor: 'colorFeedbackInformation',
  color: 'colorPrimary03',
  boxShadow: 2,
  borderRadius: 6
}));
/**
 * @deprecated Please do not use this component in new code. It will be removed in a future release.
 */
var OurPick = function OurPick() {
  var t = useLocaleText();
  return /*#__PURE__*/React.createElement(StyledPill$1, null, /*#__PURE__*/React.createElement(IconSystemHeart, {
    width: OUR_PICK_ICON_SIZE,
    height: OUR_PICK_ICON_SIZE
  }), /*#__PURE__*/React.createElement(Text, {
    typography: "typeBodyCaption"
  }, t('our_pick')));
};

var PageError = function PageError(_ref) {
  var title = _ref.title,
    links = _ref.links,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    mb: 8
  }, /*#__PURE__*/React.createElement(Illustration404, {
    width: "246px"
  })), /*#__PURE__*/React.createElement(Text, {
    typography: {
      xs: 'typeMobileDisplay03',
      md: 'typeDesktopDisplay04'
    },
    mb: 8
  }, title), children, links && links.map(function (_ref2) {
    var linkTitle = _ref2.linkTitle,
      linkHref = _ref2.linkHref;
    return /*#__PURE__*/React.createElement(CustomLink, {
      to: linkHref,
      key: linkTitle
    }, linkTitle);
  }));
};

var _excluded$6 = ["label", "variant", "icon", "buttonIcon"];
var _templateObject$c, _templateObject2$a;
var variantsMap = {
  "default": {
    bgColor: 'colorPillUSP',
    color: 'colorTextLabel'
  },
  marketing: {
    bgColor: 'colorPillMarketing',
    color: 'colorTextInverted'
  },
  price: {
    bgColor: 'colorPillPrice',
    color: 'colorTextInverted'
  },
  eco: {
    bgColor: 'colorPillEco',
    color: 'colorTextInverted'
  },
  membership: {
    bgColor: 'colorPillMembership',
    color: 'colorTextInverted'
  },
  sponsored: {
    bgColor: 'colorPillSponsored',
    color: 'colorTextBody'
  }
};
var StyledPill = styled(Flex)(_templateObject$c || (_templateObject$c = _taggedTemplateLiteral(["\n  background-color: ", ";\n  color: ", ";\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  word-break: break-word;\n  text-align: center;\n  ", ";\n  ", ";\n"])), function (_ref) {
  var theme = _ref.theme,
    bgColor = _ref.bgColor;
  return theme.colors[bgColor];
}, function (_ref2) {
  var theme = _ref2.theme,
    color = _ref2.color;
  return theme.colors[color];
}, system({
  py: 3,
  px: 5,
  borderRadius: 4,
  gap: '4px'
}), sizingSpacing);
var StyledPillIcon = styled.span(_templateObject2$a || (_templateObject2$a = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  width: 'iconSmall',
  height: 'iconSmall'
}));
var Pill = function Pill(_ref3) {
  var label = _ref3.label,
    variant = _ref3.variant,
    icon = _ref3.icon,
    buttonIcon = _ref3.buttonIcon,
    props = _objectWithoutProperties(_ref3, _excluded$6);
  var _ref4 = variant ? variantsMap[variant] : variantsMap["default"],
    bgColor = _ref4.bgColor,
    color = _ref4.color;
  return /*#__PURE__*/React.createElement(StyledPill, _extends({}, props, {
    bgColor: bgColor,
    color: color,
    typography: "typeBodyCaption"
  }), icon && /*#__PURE__*/React.createElement(StyledPillIcon, {
    "data-testid": "lp-components-pill-icon",
    role: "presentation"
  }, icon), label, buttonIcon && /*#__PURE__*/React.createElement(StyledPillIcon, {
    "data-testid": "lp-components-pill-button-icon",
    role: "button",
    style: {
      cursor: 'pointer'
    }
  }, buttonIcon));
};

var _templateObject$b, _templateObject2$9;
var Z_INDEX_POPOVER = 2000;
var StyledPopoverOverlay = styled.div(_templateObject$b || (_templateObject$b = _taggedTemplateLiteral(["\n  position: fixed;\n  top: 0;\n  bottom: 0;\n  left: 0;\n  right: 0;\n  z-index: ", ";\n  max-height: 100vh;\n  background-color: rgba(0, 0, 0, 0.6);\n"])), Z_INDEX_POPOVER);
var StyledPopoverContent = styled(Box)(_templateObject2$9 || (_templateObject2$9 = _taggedTemplateLiteral(["\n  z-index: ", ";\n  ", "\n\n  span svg {\n    ", "\n  }\n\n  &:focus-visible {\n    outline: none;\n  }\n"])), Z_INDEX_POPOVER, system({
  boxShadow: 2,
  borderRadius: 4,
  padding: 8
}), function (_ref) {
  var bgcolor = _ref.bgcolor;
  return system({
    color: bgcolor
  });
});
var PopoverArrow = function PopoverArrow() {
  return /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("svg", {
    display: "block",
    width: "32",
    height: "8",
    viewBox: "0 0 30 10",
    preserveAspectRatio: "none",
    fill: "currentColor"
  }, /*#__PURE__*/React.createElement("polygon", {
    points: "0,0 30,0 15,10"
  })));
};
var Trigger = function Trigger(_ref2) {
  var children = _ref2.children;
  return /*#__PURE__*/React.createElement(RadixPopover.Trigger, {
    asChild: true
  }, children);
};
var Anchor = function Anchor(_ref3) {
  var virtualRef = _ref3.virtualRef,
    asChild = _ref3.asChild,
    children = _ref3.children;
  return asChild ? /*#__PURE__*/React.createElement(RadixPopover.Anchor, {
    virtualRef: virtualRef,
    asChild: true
  }, children) : /*#__PURE__*/React.createElement(RadixPopover.Anchor, {
    virtualRef: virtualRef
  });
};
var Content = function Content(_ref4) {
  var children = _ref4.children,
    align = _ref4.align,
    bgcolor = _ref4.bgcolor,
    side = _ref4.side,
    onDismiss = _ref4.onDismiss,
    testId = _ref4.testId,
    withOverlay = _ref4.withOverlay,
    id = _ref4.id;
  return /*#__PURE__*/React.createElement(RadixPopover.Portal, null, /*#__PURE__*/React.createElement(React.Fragment, null, withOverlay && /*#__PURE__*/React.createElement(StyledPopoverOverlay, {
    "data-testid": "".concat(testId, "-overlay")
  }), /*#__PURE__*/React.createElement(RadixPopover.Content, _extends({
    side: side,
    align: align
  }, id && {
    id: id
  }, {
    onEscapeKeyDown: onDismiss,
    onPointerDownOutside: onDismiss,
    asChild: true,
    "data-testid": "".concat(testId, "-content")
  }), /*#__PURE__*/React.createElement(StyledPopoverContent, {
    bgcolor: bgcolor
  }, React.Children.map(children, function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      onDismiss: onDismiss
    });
  }), /*#__PURE__*/React.createElement(RadixPopover.Arrow, {
    asChild: true
  }, /*#__PURE__*/React.createElement(PopoverArrow, null))))));
};
var Popover = function Popover(_ref5) {
  var _ref5$align = _ref5.align,
    align = _ref5$align === void 0 ? 'center' : _ref5$align,
    _ref5$bgcolor = _ref5.bgcolor,
    bgcolor = _ref5$bgcolor === void 0 ? 'colorBackground03' : _ref5$bgcolor,
    children = _ref5.children,
    isOpen = _ref5.isOpen,
    onDismiss = _ref5.onDismiss,
    _ref5$side = _ref5.side,
    side = _ref5$side === void 0 ? 'bottom' : _ref5$side,
    _ref5$testId = _ref5.testId,
    testId = _ref5$testId === void 0 ? 'lp-popover' : _ref5$testId,
    withOverlay = _ref5.withOverlay;
  return /*#__PURE__*/React.createElement(RadixPopover.Root, {
    open: isOpen
  }, React.Children.map(children, function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      align: align,
      bgcolor: bgcolor,
      onDismiss: onDismiss,
      side: side,
      testId: testId,
      withOverlay: withOverlay
    });
  }));
};
Popover.Content = Content;
Popover.Trigger = Trigger;
Popover.Anchor = Anchor;

var _excluded$5 = ["children", "testId", "loading", "leadingIcon", "trailingIcon"];
var _templateObject$a, _templateObject2$8, _templateObject3$4;
var primaryButtonStyles = css(_templateObject$a || (_templateObject$a = _taggedTemplateLiteral(["\n  ", "\n\n  ", "\n\n  ", "\n\n  &.loading {\n    text-decoration: none;\n    cursor: progress;\n    ", "\n  }\n\n  &:not(:disabled, .loading) {\n    &:hover,\n    &:focus {\n      text-decoration: none;\n    }\n\n    &:hover {\n      ", "\n    }\n\n    &:focus {\n      ", "\n    }\n\n    &:active {\n      ", "\n    }\n  }\n"])), commonStyles$1, system({
  border: 2,
  borderColor: 'colorInteractionButton',
  bgcolor: 'colorInteractionButton',
  color: 'colorTextInverted'
}), sizingSpacing, system({
  bgcolor: 'colorBackgroundDeactivated',
  color: 'colorInteractionLoading',
  borderColor: 'colorInteractionDeactivated'
}), system({
  borderColor: 'colorInteractionButtonHover',
  bgcolor: 'colorInteractionButtonHover'
}), system({
  borderColor: 'colorInteractionButtonFocus',
  bgcolor: 'colorInteractionButtonFocus'
}), function () {
  return system({
    borderColor: 'colorInteractionButtonFocus',
    bgcolor: 'colorInteractionButtonFocus'
  });
});
var StyledButton$1 = styled.button(_templateObject2$8 || (_templateObject2$8 = _taggedTemplateLiteral(["\n  ", "\n"])), primaryButtonStyles);
var StyledLink$1 = styled(Link)(_templateObject3$4 || (_templateObject3$4 = _taggedTemplateLiteral(["\n  ", "\n"])), primaryButtonStyles);
var PrimaryButton = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
    _props$testId = props.testId,
    testId = _props$testId === void 0 ? 'lp-components-primary-button' : _props$testId,
    loading = props.loading,
    leadingIcon = props.leadingIcon,
    trailingIcon = props.trailingIcon,
    rest = _objectWithoutProperties(props, _excluded$5);
  var t = useLocaleText();
  var ariaLive = loading ? 'polite' : 'off';
  var ariaLabel = loading ? t('common.loading') : undefined;
  var descendants = /*#__PURE__*/React.createElement(ButtonContentContainer, null, loading && /*#__PURE__*/React.createElement(ButtonLoadingIndicator, null), !loading && leadingIcon && /*#__PURE__*/React.createElement(ButtonIconContainer, {
    icon: leadingIcon,
    testId: "".concat(testId, "-leading-icon")
  }), children, !loading && trailingIcon && /*#__PURE__*/React.createElement(ButtonIconContainer, {
    icon: trailingIcon,
    testId: "".concat(testId, "-trailing-icon")
  }));
  if ('href' in props) {
    return /*#__PURE__*/React.createElement(StyledLink$1, _extends({
      ref: ref,
      "data-testid": testId,
      "aria-live": ariaLive,
      "aria-busy": !!loading,
      disabled: !!loading,
      "aria-label": ariaLabel,
      style: {
        cursor: loading ? 'progress' : 'pointer'
      },
      className: loading ? 'loading' : ''
    }, rest), descendants);
  }
  return /*#__PURE__*/React.createElement(StyledButton$1, _extends({
    ref: ref,
    "data-testid": testId,
    "aria-live": ariaLive,
    "aria-busy": !!loading,
    disabled: !!loading,
    "aria-label": loading ? t('common.loading') : undefined,
    className: loading ? 'loading' : ''
  }, rest), descendants);
});
PrimaryButton.displayName = 'PrimaryButton';

var _templateObject$9, _templateObject2$7, _templateObject3$3, _templateObject4$2, _templateObject5$2;
var PROGRESS_STATE_WIDTHS = {
  completed: '100%',
  incomplete: '0%'
};
var StepStatus = /*#__PURE__*/function (StepStatus) {
  StepStatus["active"] = "active";
  StepStatus["completed"] = "completed";
  StepStatus["incomplete"] = "incomplete";
  return StepStatus;
}({});
var StyledList = styled.ol(_templateObject$9 || (_templateObject$9 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: ", ";\n"])), spacingPx(3));
var StyledListItem = styled.li(_templateObject2$7 || (_templateObject2$7 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  flex-grow: 1;\n  gap: ", ";\n\n  &:last-child {\n    flex-grow: 0;\n  }\n"])), spacingPx(3));
var StyledStepPoint = styled.div(_templateObject3$3 || (_templateObject3$3 = _taggedTemplateLiteral(["\n  display: flex;\n  width: ", ";\n  height: ", ";\n  border-radius: 100px;\n  flex-shrink: 0;\n\n  ", ";\n\n  .lp-progress-indicator-item--completed &,\n  .lp-progress-indicator-item--active & {\n    ", "\n  }\n\n  .lp-progress-indicator-item--completed & {\n    ", "\n  }\n\n  .lp-progress-indicator--all-completed & {\n    ", "\n  }\n"])), function (props) {
  return props.minimal ? spacingPx(4) : spacingPx(8);
}, function (props) {
  return props.minimal ? spacingPx(4) : spacingPx(8);
}, system({
  border: 4,
  borderColor: 'colorBlack20',
  color: 'colorBlack00'
}), system({
  borderColor: 'colorInteractionSelectedState'
}), system({
  bgcolor: 'colorInteractionSelectedState'
}), system({
  borderColor: 'colorFeedbackSuccess',
  bgcolor: 'colorFeedbackSuccess'
}));
var StyledStepProgressBar = styled.div(_templateObject4$2 || (_templateObject4$2 = _taggedTemplateLiteral(["\n  height: ", ";\n  width: 100%;\n  border-radius: 100px;\n  overflow: hidden;\n\n  ", "\n"])), spacingPx(4), system({
  bgcolor: 'colorBlack20'
}));
var StyledStepProgressState = styled(Box)(_templateObject5$2 || (_templateObject5$2 = _taggedTemplateLiteral(["\n  height: 100%;\n  border-radius: 100px;\n\n  ", "\n\n  .lp-progress-indicator--all-completed & {\n    ", "\n  }\n"])), system({
  bgcolor: 'colorInteractionSelectedState'
}), system({
  bgcolor: 'colorFeedbackSuccess'
}));
var ProgressIndicator = function ProgressIndicator(_ref) {
  var steps = _ref.steps,
    _ref$minimal = _ref.minimal,
    minimal = _ref$minimal === void 0 ? false : _ref$minimal;
  var t = useLocaleText();
  var activeStepIndex = steps.findIndex(function (step) {
    return step.status === 'active';
  });
  var activeStepOrLastIndex = activeStepIndex > -1 ? activeStepIndex : steps.length - 1;
  var activeStep = steps[activeStepOrLastIndex];
  var allComplete = steps.every(function (step) {
    return step.status === 'completed';
  });
  var indexedProgressLabel = function indexedProgressLabel(stepIndex) {
    return "".concat(stepIndex + 1, " ").concat(t('progress-indicator-of'), " ").concat(steps.length);
  };
  var activeIndexProgressLabel = indexedProgressLabel(activeStepOrLastIndex);
  var activeStepProgressLabel = "".concat(t('progress-indicator-step'), " ").concat(activeIndexProgressLabel);
  return /*#__PURE__*/React.createElement(VerticalStack, {
    role: "group",
    spacing: 4
  }, /*#__PURE__*/React.createElement(StyledList, {
    role: "list",
    className: allComplete ? 'lp-progress-indicator--all-completed' : ''
  }, steps.map(function (step, stepIndex) {
    var stepHelperText = "".concat(t('progress-indicator-step'), ": ").concat(step.label || indexedProgressLabel(stepIndex));
    var progressWidth = step.status === 'active' ? "".concat(step.progressPercentage, "%") : PROGRESS_STATE_WIDTHS[step.status];
    var renderProgressBar = stepIndex !== steps.length - 1;

    // eslint-disable-next-line react/no-array-index-key
    return /*#__PURE__*/React.createElement(StyledListItem, {
      key: step.label || "step-".concat(stepIndex),
      "aria-current": step.status === 'active',
      title: step.label || stepHelperText,
      className: "lp-progress-indicator-item--".concat(step.status)
    }, /*#__PURE__*/React.createElement(StyledStepPoint, {
      role: "presentation",
      minimal: minimal
    }, step.status === 'completed' && /*#__PURE__*/React.createElement(IconSystemTick, null)), renderProgressBar && /*#__PURE__*/React.createElement(StyledStepProgressBar, {
      role: "presentation"
    }, /*#__PURE__*/React.createElement(StyledStepProgressState, {
      "data-testid": "lp-progress-indicator-state--".concat(stepIndex),
      width: progressWidth
    })), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "".concat(t("progress-indicator-".concat(step.status)), " ").concat(stepHelperText)));
  })), activeStep.renderLabel ? activeStep.renderLabel({
    activeStep: activeStepOrLastIndex + 1,
    totalSteps: steps.length
  }) : /*#__PURE__*/React.createElement(Text, {
    as: "p",
    mb: 0,
    "data-testid": "lp-progress-indicator-label"
  }, /*#__PURE__*/React.createElement(Text, {
    typography: "typeBodyLabel",
    color: "colorTextHeadline",
    "aria-hidden": true
  }, "".concat(activeStepProgressLabel).concat(activeStep.label ? ':' : '', " ")), /*#__PURE__*/React.createElement(Text, {
    typography: "typeBodyText",
    color: "colorTextBody",
    "aria-hidden": true
  }, activeStep.label)));
};

var _templateObject$8, _templateObject2$6;
var StyledStar = styled.div(_templateObject$8 || (_templateObject$8 = _taggedTemplateLiteral(["\n  ", "\n\n  svg {\n    display: block;\n  }\n"])), system({
  position: 'relative',
  ml: -2
}));
var StyledStarFilled = styled.div(_templateObject2$6 || (_templateObject2$6 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref) {
  var width = _ref.width,
    color = _ref.color;
  return system({
    width: "".concat(width, "%"),
    height: '100%',
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
    color: color
  });
});
var Star = function Star(_ref2) {
  var size = _ref2.size,
    width = _ref2.width,
    filledColor = _ref2.filledColor;
  return /*#__PURE__*/React.createElement(StyledStar, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(IconSystemStar, {
    width: size,
    height: size
  }), /*#__PURE__*/React.createElement(StyledStarFilled, {
    "data-testid": "lp-components-rating-filled-star",
    width: width,
    color: filledColor
  }, /*#__PURE__*/React.createElement(IconSystemStar, {
    "data-testid": "lp-components-rating-filled-icon-star",
    width: size,
    height: size
  })));
};

var _templateObject$7, _templateObject2$5;
var StyledRatingCount = styled.span(_templateObject$7 || (_templateObject$7 = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  typography: 'typeBodyCaption',
  ml: 4,
  color: 'colorTextBody'
}));
var StyledStars = styled.div(_templateObject2$5 || (_templateObject2$5 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref) {
  var color = _ref.color;
  return system({
    display: 'flex',
    color: color,
    ml: 2
  });
});
var calculateWidthPercentage = function calculateWidthPercentage(baseWidth) {
  var safeArea = 0.11;
  return (baseWidth * (1 - safeArea * 2) + safeArea) * 100;
};
var createFilledWidth = function createFilledWidth(rating, index) {
  var ratingInteger = Math.floor(rating);
  if (index > ratingInteger) {
    return 0;
  }
  if (index < ratingInteger) {
    return 100;
  }
  var ratingDecimal = rating % ratingInteger;
  return calculateWidthPercentage(rating < 1 ? rating : ratingDecimal);
};
var Rating = function Rating(_ref2) {
  var rating = _ref2.rating,
    totalRatingsText = _ref2.totalRatingsText,
    totalRatingsScreenReaderText = _ref2.totalRatingsScreenReaderText,
    totalRatingsTextId = _ref2.totalRatingsTextId,
    _ref2$filledStarColor = _ref2.filledStarColor,
    filledStarColor = _ref2$filledStarColor === void 0 ? 'colorFeedbackRating' : _ref2$filledStarColor,
    _ref2$emptyStarColor = _ref2.emptyStarColor,
    emptyStarColor = _ref2$emptyStarColor === void 0 ? 'colorBlack10' : _ref2$emptyStarColor,
    starSize = _ref2.starSize;
  var t = useLocaleText();
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    id: totalRatingsTextId
  }, /*#__PURE__*/React.createElement(StyledStars, {
    "data-testid": "lp-components-rating-stars",
    "aria-label": t('rating.rated', rating.toFixed(1)),
    color: emptyStarColor,
    role: "img"
  }, _toConsumableArray(Array(5)).map(function (_, index) {
    return /*#__PURE__*/React.createElement(Star
    // eslint-disable-next-line react/no-array-index-key
    , {
      key: index,
      size: starSize,
      filledColor: filledStarColor,
      width: createFilledWidth(rating, index)
    });
  })), (totalRatingsText || totalRatingsScreenReaderText) && /*#__PURE__*/React.createElement(React.Fragment, null, totalRatingsText && /*#__PURE__*/React.createElement(StyledRatingCount, {
    "data-testid": "lp-components-rating-count"
  }, totalRatingsText), totalRatingsScreenReaderText && /*#__PURE__*/React.createElement(ScreenReaderOnly, null, totalRatingsScreenReaderText)));
};

var _excluded$4 = ["children"];
var _templateObject$6;
var StyledContainer = styled.div(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteral(["\n  ", ";\n  box-sizing: content-box;\n  word-break: break-word;\n  > div {\n    box-sizing: border-box;\n  }\n\n  & > * {\n    ", "\n  }\n\n  ol {\n    ", "\n\n    display: block;\n    list-style-position: inside;\n    list-style-type: decimal;\n  }\n\n  ol ol {\n    ", "\n\n    list-style-type: lower-roman;\n  }\n\n  ol ol ol {\n    list-style-type: lower-alpha;\n  }\n\n  ul {\n    ", "\n\n    display: block;\n    list-style-position: inside;\n    list-style-type: disc;\n  }\n\n  ul ul {\n    ", "\n\n    list-style-type: circle;\n  }\n\n  ul ul ul {\n    list-style-type: square;\n  }\n\n  li > p {\n    display: inline;\n    line-height: 2;\n  }\n\n  h1 {\n    text-align: center;\n  }\n\n  a {\n    font-weight: bold;\n  }\n\n  img {\n    display: block;\n    margin-left: auto;\n    margin-right: auto;\n    max-width: 100%;\n  }\n"])), function (_ref) {
  var richTextWidth = _ref.theme.richTextWidth;
  return system({
    maxWidth: richTextWidth,
    mx: 'auto',
    px: {
      xs: 6,
      md: 8
    }
  });
}, system({
  mt: 0,
  mb: 6
}), system({
  pl: 6
}), system({
  pl: 10
}), system({
  pl: 6
}), system({
  pl: 10
}));
var RichTextContainer = function RichTextContainer(_ref2) {
  var children = _ref2.children,
    rest = _objectWithoutProperties(_ref2, _excluded$4);
  return /*#__PURE__*/React.createElement(StyledContainer, _extends({
    limitWidth: true,
    padding: true
  }, rest), /*#__PURE__*/React.createElement("div", null, children));
};

var _excluded$3 = ["children", "testId", "loading", "leadingIcon", "trailingIcon"];
var _templateObject$5, _templateObject2$4, _templateObject3$2;
var secondaryButtonStyles = css(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteral(["\n  ", "\n\n  ", "\n\n  ", "\n\n  &.loading {\n    text-decoration: none;\n    cursor: progress;\n    ", "\n  }\n\n  &:not(:disabled, .loading) {\n    &:hover,\n    &:focus {\n      text-decoration: none;\n    }\n\n    &:hover {\n      ", "\n    }\n\n    &:focus {\n      ", "\n    }\n\n    &:active {\n      ", "\n    }\n  }\n"])), commonStyles$1, system({
  bgcolor: 'colorBackground01',
  color: 'colorInteractionButton',
  borderRadius: 2,
  border: 2,
  borderColor: 'colorInteractionButton'
}), sizingSpacing, system({
  bgcolor: 'colorBackgroundDeactivated',
  color: 'colorInteractionLoading',
  borderColor: 'colorInteractionDeactivated'
}), system({
  bgcolor: 'colorInteractionBackgroundHover',
  color: 'colorInteractionButtonHover'
}), system({
  bgcolor: 'colorInteractionBackgroundHover',
  color: 'colorInteractionButtonFocus'
}), function () {
  return system({
    bgcolor: 'colorInteractionBackgroundHover',
    color: 'colorInteractionButtonFocus'
  });
});
var StyledButton = styled.button(_templateObject2$4 || (_templateObject2$4 = _taggedTemplateLiteral(["\n  ", "\n"])), secondaryButtonStyles);
var StyledLink = styled(Link)(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteral(["\n  ", "\n"])), secondaryButtonStyles);
var SecondaryButton = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
    _props$testId = props.testId,
    testId = _props$testId === void 0 ? 'lp-components-secondary-button' : _props$testId,
    loading = props.loading,
    leadingIcon = props.leadingIcon,
    trailingIcon = props.trailingIcon,
    rest = _objectWithoutProperties(props, _excluded$3);
  var t = useLocaleText();
  var ariaLive = loading ? 'polite' : 'off';
  var ariaLabel = loading ? t('common.loading') : undefined;
  var descendants = /*#__PURE__*/React.createElement(ButtonContentContainer, null, loading && /*#__PURE__*/React.createElement(ButtonLoadingIndicator, null), !loading && leadingIcon && /*#__PURE__*/React.createElement(ButtonIconContainer, {
    icon: leadingIcon,
    testId: "".concat(testId, "-leading-icon")
  }), children, trailingIcon && /*#__PURE__*/React.createElement(ButtonIconContainer, {
    icon: trailingIcon,
    testId: "".concat(testId, "-trailing-icon")
  }));
  if ('href' in props) {
    return /*#__PURE__*/React.createElement(StyledLink, _extends({
      ref: ref,
      "data-testid": testId,
      "aria-live": ariaLive,
      "aria-busy": !!loading,
      disabled: !!loading,
      "aria-label": ariaLabel,
      style: {
        cursor: loading ? 'progress' : 'pointer'
      },
      className: loading ? 'loading' : ''
    }, rest), descendants);
  }
  return /*#__PURE__*/React.createElement(StyledButton, _extends({
    ref: ref,
    "data-testid": testId,
    "aria-live": ariaLive,
    "aria-busy": !!loading,
    disabled: !!loading,
    "aria-label": ariaLabel,
    className: loading ? 'loading' : ''
  }, rest), descendants);
});
SecondaryButton.displayName = 'SecondaryButton';

var _excluded$2 = ["children", "testId"];
var _templateObject$4;
var StyledStrip = styled.div(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: center;\n  ", "\n\n  > span {\n    ", "\n    width: 100%;\n    text-align: center;\n  }\n"])), sizingSpacing, system({
  px: 6,
  py: 4,
  typography: {
    xs: 'typeMobileDisplay05',
    md: 'typeDesktopDisplay06'
  },
  color: 'colorTextBody',
  bgcolor: 'colorBackground05'
}));
var Strip = function Strip(_ref) {
  var children = _ref.children,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? 'lp-components-strip' : _ref$testId,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return /*#__PURE__*/React.createElement(StyledStrip, _extends({
    "data-testid": testId
  }, props), /*#__PURE__*/React.createElement("span", null, children));
};

var _templateObject$3, _templateObject2$3, _templateObject3$1, _templateObject4$1, _templateObject5$1, _templateObject6$1, _templateObject7$1, _templateObject8, _templateObject9, _templateObject0, _templateObject1, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14;
var SCROLLBAR_WIDTH = 20;
var fadeIn = keyframes(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteral(["\n  from {\n    opacity: 0;\n  }\n  to {\n    opacity: 1;\n  }\n"])));
var fadeOut = keyframes(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteral(["\n  from {\n    opacity: 1;\n  }\n  to {\n    opacity: 0;\n  }\n"])));
var slideInRight = keyframes(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n  from {\n    transform: translateX(100%);\n    opacity: 0.5;\n  }\n  to {\n    transform: translateX(0);\n    opacity: 1;\n  }\n"])));
var slideInLeft = keyframes(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteral(["\n    from {\n      transform: translateX(-100%);\n      opacity: 0.5;\n    }\n    to {\n      transform: translateX(0);\n      opacity: 1;\n    }\n  "])));
var slideOutRight = keyframes(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteral(["\n  from {\n    transform: translateX(0);\n    opacity: 1;\n  }\n  to {\n    transform: translateX(100%);\n    opacity: 0.5;\n  }\n"])));
var slideOutLeft = keyframes(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteral(["\n  from {\n    transform: translateX(0);\n    opacity: 1;\n  }\n  to {\n    transform: translateX(-100%);\n    opacity: 0.5;\n  }\n"])));
var animationBezier = 'cubic-bezier(0.56, 0.1, 0.34, 0.91)';
var slideInAnimation = function slideInAnimation(side) {
  return css(_templateObject7$1 || (_templateObject7$1 = _taggedTemplateLiteral(["\n  animation: 450ms ", "\n    ", " forwards;\n"])), animationBezier, side === 'left' ? slideInLeft : slideInRight);
};

// istanbul ignore next
var slideOutAnimation = function slideOutAnimation(side) {
  return css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n  animation: 450ms ", " /* istanbul ignore next */\n    ", " forwards;\n"])), animationBezier, side === 'left' ? slideOutLeft : slideOutRight);
};
var fadeInAnimation = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n  animation: 450ms ", " ", " forwards;\n"])), animationBezier, fadeIn);
var fadeOutAnimation = css(_templateObject0 || (_templateObject0 = _taggedTemplateLiteral(["\n  animation: 450ms ", " ", " forwards;\n"])), animationBezier, fadeOut);
var StyledDialogOverlay = styled.div(_templateObject1 || (_templateObject1 = _taggedTemplateLiteral(["\n  position: fixed;\n  inset: 0;\n\n  overflow-y: auto;\n  z-index: 1000;\n\n  &::before {\n    background: rgba(0, 0, 0, 0.6);\n    content: ' ';\n    position: fixed;\n    inset: 0;\n  }\n\n  ", "\n\n  @media (prefers-reduced-motion) {\n    animation: none;\n  }\n"])), function (_ref) {
  var isClosing = _ref.isClosing;
  return isClosing !== null && (isClosing ? fadeOutAnimation : fadeInAnimation);
});
var StyledDialogContent = styled(Box)(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n  position: absolute;\n  right: 0;\n  display: flex;\n  flex-direction: column;\n\n  ", ";\n\n  ", "\n  min-height: 100%;\n  z-index: 1000;\n\n  ", ";\n\n  @media (prefers-reduced-motion) {\n    animation: none;\n  }\n"])), function (_ref2) {
  var side = _ref2.side;
  return side === 'left' ? {
    left: 0
  } : {
    right: 0
  };
}, function (_ref3) {
  var backgroundColor = _ref3.backgroundColor;
  return system({
    bgcolor: backgroundColor || 'colorBackgroundSite',
    p: 6
  });
}, function (_ref4) {
  var isClosing = _ref4.isClosing,
    side = _ref4.side;
  return isClosing !== null && side && (isClosing ? slideOutAnimation(side) : slideInAnimation(side));
});
var StyledDialogCloseWrapper = styled.div(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: ", ";\n  justify-content: space-between;\n  align-items: center;\n  min-height: 48px;\n  position: relative;\n  z-index: 1;\n  gap: ", ";\n\n  ", ";\n"])), function (_ref5) {
  var side = _ref5.side;
  return side === 'left' ? 'row-reverse' : 'row';
}, spacingPx(6), system({
  mb: 4
}));
var StyledBackButton = styled(IconButton)(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref6) {
  var side = _ref6.side;
  return system({
    mr: side === 'left' ? -4 : 'auto',
    ml: side === 'left' ? 'auto' : -4
  });
});
var StyledDialogClose = styled(Close)(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref7) {
  var side = _ref7.side;
  return system({
    mr: side === 'left' ? 'auto' : -4,
    ml: side === 'left' ? -4 : 'auto'
  });
});
var StyledHeading = styled.h2(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  typography: {
    xs: 'typeMobileDisplay03',
    md: 'typeDesktopDisplay05'
  },
  m: 0
}));
var SideModal = function SideModal(_ref8) {
  var isOpen = _ref8.isOpen,
    onDismiss = _ref8.onDismiss,
    children = _ref8.children,
    heading = _ref8.heading,
    description = _ref8.description,
    backgroundColor = _ref8.backgroundColor,
    backButton = _ref8.backButton,
    onBack = _ref8.onBack,
    labels = _ref8.labels,
    _ref8$width = _ref8.width,
    width = _ref8$width === void 0 ? {
      xs: '100%',
      md: '33%'
    } : _ref8$width,
    _ref8$maxWidth = _ref8.maxWidth,
    maxWidth = _ref8$maxWidth === void 0 ? {
      md: '450px'
    } : _ref8$maxWidth,
    _ref8$minWidth = _ref8.minWidth,
    minWidth = _ref8$minWidth === void 0 ? {
      md: '375px'
    } : _ref8$minWidth,
    triggerRef = _ref8.triggerRef,
    _ref8$side = _ref8.side,
    side = _ref8$side === void 0 ? 'right' : _ref8$side;
  var localiseText = useLocaleText();
  var _useState = useState(isOpen),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isClosing = _useState4[0],
    setIsClosing = _useState4[1];
  useEffect(function () {
    setOpen(isOpen);
    setIsClosing(false);
  }, [isOpen]);
  var firstUpdate = useRef(true);
  useEffect(function () {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (!isOpen && triggerRef !== null && triggerRef !== void 0 && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [isOpen, triggerRef]);
  var renderHeading = function renderHeading() {
    if (heading) {
      if (typeof heading === 'string') {
        return /*#__PURE__*/React.createElement(StyledHeading, null, heading);
      }
      return heading;
    }
    return /*#__PURE__*/React.createElement(ScreenReaderOnly, null, localiseText('modal.description'));
  };
  return /*#__PURE__*/React.createElement(Root$2, {
    open: open,
    onOpenChange: function onOpenChange() {
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reduceMotion.matches) {
        setOpen(!open);
        onDismiss(!open);
        return;
      }
      setIsClosing(true);
    }
  }, /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(Overlay, {
    asChild: true,
    className: "DialogOverlay"
  }, /*#__PURE__*/React.createElement(StyledDialogOverlay, {
    isClosing: isClosing,
    "data-testid": "lpd-side-modal-overlay"
  }, /*#__PURE__*/React.createElement(Content$2, {
    asChild: true,
    onPointerDownOutside: /* istanbul ignore next */function onPointerDownOutside(e) {
      var currentTarget = e.currentTarget;
      // Hack to prevent clicking the scrollbar from closing the modal
      // Needs to be revisited
      if (e.detail.originalEvent.offsetX > currentTarget.clientWidth - SCROLLBAR_WIDTH) {
        e.preventDefault();
      }
    }
  }, /*#__PURE__*/React.createElement(StyledDialogContent, {
    "data-testid": "lpd-side-modal-content",
    width: width,
    minWidth: minWidth,
    maxWidth: maxWidth,
    isClosing: isClosing,
    backgroundColor: backgroundColor,
    onAnimationEnd: function onAnimationEnd() {
      if (isClosing) {
        setOpen(!open);
        onDismiss(!open);
      }
    },
    side: side
  }, /*#__PURE__*/React.createElement(StyledDialogCloseWrapper, {
    side: side
  }, backButton && onBack && /*#__PURE__*/React.createElement(StyledBackButton, {
    icon: side === 'left' ? IconSystemChevronRight : IconSystemChevronLeft,
    label: (labels === null || labels === void 0 ? void 0 : labels.backButton) || localiseText('common.side-modal.back.label'),
    onClick: onBack,
    side: side
  }), /*#__PURE__*/React.createElement(Title, {
    asChild: true
  }, renderHeading()), /*#__PURE__*/React.createElement(StyledDialogClose, {
    asChild: true,
    side: side
  }, /*#__PURE__*/React.createElement(IconButton, {
    icon: IconSystemCross,
    label: (labels === null || labels === void 0 ? void 0 : labels.closeButton) || localiseText('common.side-modal.close.label')
  }))), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement(Description, null, description || localiseText('modal.description'))), children))))));
};

var _excluded$1 = ["animationDuration", "animationDelay", "screenReaderText", "colours", "id"];
var _templateObject$2, _templateObject2$2;
var INITIAL_BACKGROUND_COLOUR = colorValue('colorBlack20');
var EFFECT_BACKGROUND_COLOUR = colorValue('colorBlack10');
var StyledBox = styled(function (props) {
  return /*#__PURE__*/React.createElement(Box, props);
})(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  ", "\n"])), function (props) {
  return "\n    background-color: ".concat(props.colours ? colorValue(props.colours.background)({
    theme: props.theme
  }) : INITIAL_BACKGROUND_COLOUR({
    theme: props.theme
  }), ";\n    position: relative;\n    overflow: hidden;\n\n    &:after {\n      animation: shimmer ").concat(props.animationDuration, "s linear ").concat(props.animationDelay, "s infinite;\n      background: linear-gradient(90deg, transparent, ").concat(props.colours ? colorValue(props.colours.effect)({
    theme: props.theme
  }) : EFFECT_BACKGROUND_COLOUR({
    theme: props.theme
  }), ", transparent);\n      position: absolute;\n      content: '';\n      transform: translateX(-100%);\n      bottom: 0;\n      left: 0;\n      right: 0;\n      top: 0;\n    }\n\n    @media (prefers-reduced-motion: reduce) {\n      &:after {\n        animation: none;\n      }\n    }\n\n  @keyframes shimmer {\n    0% {\n      transform: translateX(-100%);\n    }\n    60% {\n      transform: translateX(100%);\n    }\n    100% {\n      transform: translateX(100%);\n    }\n  }\n");
});

/* stylelint-disable property-no-vendor-prefix */
var StyledProgress = styled.progress(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteral(["\n  &:indeterminate {\n    -webkit-appearance: none;\n    -moz-appearance: none;\n    appearance: none;\n    background: none;\n    border: 0;\n    width: 0;\n    height: 0;\n\n    &::-moz-progress-bar {\n      background: none;\n    }\n\n    &::-webkit-progress-bar {\n      display: none;\n    }\n\n    &::-ms-fill {\n      animation-name: none;\n    }\n  }\n"])));
var SkeletonBox = function SkeletonBox(_ref) {
  var _ref$animationDuratio = _ref.animationDuration,
    animationDuration = _ref$animationDuratio === void 0 ? 1.6 : _ref$animationDuratio,
    _ref$animationDelay = _ref.animationDelay,
    animationDelay = _ref$animationDelay === void 0 ? 0.5 : _ref$animationDelay,
    screenReaderText = _ref.screenReaderText,
    colours = _ref.colours,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? undefined : _ref$id,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var t = useLocaleText();
  return /*#__PURE__*/React.createElement(StyledBox, _extends({
    "data-testid": "skeleton-box",
    animationDuration: animationDuration,
    animationDelay: animationDelay,
    colours: colours
  }, props), /*#__PURE__*/React.createElement(StyledProgress, {
    id: id,
    "aria-label": screenReaderText || t('skeleton_box.loading_indicator_box')
  }));
};

var _excluded = ["children", "variant", "Icon", "badgeCount"];
var _templateObject$1, _templateObject2$1, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7;
var StyledTabList = styled(function (_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(Tabs.List, props);
})(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  display: flex;\n  gap: ", ";\n  box-sizing: border-box;\n  position: relative;\n\n  ", ";\n\n  ", "\n"])), function (_ref2) {
  var variant = _ref2.variant;
  return variant === 'chip' ? spacingPx(6) : spacingPx(4);
}, function (_ref3) {
  var variant = _ref3.variant;
  return variant === 'chip' ? system({
    justifyContent: {
      xs: 'initial',
      md: 'center'
    },
    gap: {
      xs: '8px',
      md: '16px'
    },
    marginBottom: {
      xs: 6,
      lg: 8
    },
    paddingX: {
      xs: 6,
      md: 0
    }
  }) : system({
    borderRadius: 2,
    bgcolor: 'colorBackground01',
    borderBottom: 1,
    borderColor: 'colorBorder03',
    gap: 5
  });
}, function (_ref4) {
  var variant = _ref4.variant;
  return variant === 'chip' && "\n      overflow-x: auto;\n      flex-wrap: nowrap;\n      -webkit-overflow-scrolling: touch;\n      scrollbar-width: thin;\n      scrollbar-color: #ccc transparent;\n    ";
});
var StyledBadge = styled(Text)(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  paddingX: '6px',
  paddingY: 3,
  bgcolor: 'colorBackground02',
  color: 'colorTextHeadline',
  lineHeight: '1rem',
  borderRadius: 3,
  fontWeight: 400
}));
var StyledDefaultTab = styled(Tabs.Trigger)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  flex: 1;\n  gap: ", ";\n  margin-bottom: -1px;\n  padding-bottom: 10px;\n\n  ", "\n\n  border-bottom-right-radius: 0;\n  border-bottom-left-radius: 0;\n\n  &[aria-selected='true'] {\n    ", "\n\n    ", " {\n      ", "\n    }\n\n    &:hover {\n      ", "\n\n      ", " {\n        ", "\n      }\n    }\n  }\n\n  &[aria-selected='false']:hover {\n    ", "\n\n    ", " {\n      ", "\n    }\n  }\n\n  &:focus-within {\n    outline: none;\n\n    ", "\n  }\n"])), spacingPx(4), system({
  typography: 'typeBodyLabel',
  color: 'colorTextHeadline',
  bgcolor: 'colorBackground01',
  borderRadius: 2,
  px: 4,
  pt: 5,
  borderBottom: 2,
  borderColor: 'transparent'
}), system({
  color: 'colorInteractionSelectedState',
  borderColor: 'colorInteractionSelectedState'
}), StyledBadge, system({
  bgcolor: 'colorInteractionSelectedState',
  color: 'colorTextInverted'
}), system({
  color: 'colorInteractionSelectedStateHover',
  bgcolor: 'colorBackground03',
  borderColor: 'colorInteractionSelectedStateHover'
}), StyledBadge, system({
  bgcolor: 'colorInteractionSelectedStateHover'
}), system({
  bgcolor: 'colorBlack05',
  borderColor: 'colorBorder02'
}), StyledBadge, system({
  bgcolor: 'colorBlack20'
}), system({
  boxShadow: 5
}));
var BaseChipTrigger = /*#__PURE__*/React.forwardRef(function (_ref5, ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref5), _ref5));
  return /*#__PURE__*/React.createElement(Tabs.Trigger, _extends({
    ref: ref
  }, props));
});
BaseChipTrigger.displayName = 'BaseChipTrigger';
var StyledChipTab = styled(BaseChipTrigger)(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  ", "\n"])), chipStyles);
var StyledTabPanels = styled.div(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  bgcolor: 'colorBackground01'
}));
var StyledTabPanel = styled(Tabs.Content).attrs({
  tabIndex: -1
})(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  &[data-state='inactive'] {\n    display: none;\n  }\n"])));
var StyledIcon = styled.svg(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n  flex-shrink: 0;\n\n  ", "\n"])), system({
  width: 'iconMedium',
  height: 'iconMedium'
}));
var Root = Tabs.Root;
var LabelledTabList = function LabelledTabList(_ref6) {
  var ariaLabel = _ref6.ariaLabel,
    _ref6$variant = _ref6.variant,
    variant = _ref6$variant === void 0 ? 'default' : _ref6$variant,
    children = _ref6.children;
  var t = useLocaleText();
  var defaultAriaLabel = t('tabs.tab_list.default_aria_label');
  return /*#__PURE__*/React.createElement(StyledTabList, {
    "aria-label": ariaLabel || defaultAriaLabel,
    variant: variant
  }, React.Children.map(children, function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      variant: variant
    });
  }));
};
var Tab = function Tab(_ref7) {
  var children = _ref7.children,
    variant = _ref7.variant,
    Icon = _ref7.Icon,
    badgeCount = _ref7.badgeCount,
    restProps = _objectWithoutProperties(_ref7, _excluded);
  var t = useLocaleText();
  return variant === 'chip' ? /*#__PURE__*/React.createElement(StyledChipTab, restProps, children) : /*#__PURE__*/React.createElement(StyledDefaultTab, restProps, Icon && /*#__PURE__*/React.createElement(StyledIcon, {
    as: Icon
  }), children, badgeCount !== undefined && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledBadge, {
    typography: "typeBodyCaption",
    "aria-hidden": true
  }, badgeCount), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, t('tabs.badge.count', badgeCount))));
};

var _templateObject, _templateObject2;
var StyledTooltipContent = styled(Box)(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n\n  span svg {\n    ", "\n  }\n"])), system({
  borderRadius: 6,
  boxShadow: 2,
  px: 8,
  py: 4
}), function (_ref) {
  var bgcolor = _ref.bgcolor;
  return system({
    color: bgcolor
  });
});
var StyledTooltipArrow = styled.svg(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref2) {
  var side = _ref2.side;
  return side === 'left' || side === 'right' ? "transform: translateY(-2px)" : '';
});
var TooltipArrow = function TooltipArrow(_ref3) {
  var align = _ref3.align,
    side = _ref3.side;
  return /*#__PURE__*/React.createElement(StyledTooltipArrow, {
    display: "block",
    width: "16",
    height: "12",
    viewBox: "0 0 30 10",
    preserveAspectRatio: "none",
    fill: "currentColor",
    align: align,
    side: side
  }, /*#__PURE__*/React.createElement("polygon", {
    points: "0,0 30,0 15,10"
  }));
};
var variantColorMap = {
  'high-emphasis': {
    background: 'colorFeedbackInformation',
    text: 'colorTextInverted'
  },
  'low-emphasis': {
    background: 'colorBackgroundHero',
    text: 'colorTextAlternate'
  }
};
var Tooltip = function Tooltip(_ref4) {
  var _ref4$align = _ref4.align,
    align = _ref4$align === void 0 ? 'center' : _ref4$align,
    children = _ref4.children,
    content = _ref4.content,
    _ref4$side = _ref4.side,
    side = _ref4$side === void 0 ? 'top' : _ref4$side,
    _ref4$variant = _ref4.variant,
    variant = _ref4$variant === void 0 ? 'high-emphasis' : _ref4$variant,
    _ref4$defaultOpen = _ref4.defaultOpen,
    defaultOpen = _ref4$defaultOpen === void 0 ? false : _ref4$defaultOpen;
  return /*#__PURE__*/React.createElement(RadixTooltip.Root, {
    defaultOpen: defaultOpen
  }, /*#__PURE__*/React.createElement(RadixTooltip.Trigger, {
    asChild: true
  }, children), /*#__PURE__*/React.createElement(RadixTooltip.Content, {
    align: align,
    side: side,
    asChild: true
  }, /*#__PURE__*/React.createElement(StyledTooltipContent, {
    bgcolor: variantColorMap[variant].background,
    color: variantColorMap[variant].text
  }, /*#__PURE__*/React.createElement(Text, {
    fontWeight: "bold"
  }, content), /*#__PURE__*/React.createElement(RadixTooltip.Arrow, {
    asChild: true
  }, /*#__PURE__*/React.createElement(TooltipArrow, {
    align: align,
    side: side
  })))));
};
var Provider = function Provider(_ref5) {
  var children = _ref5.children,
    _ref5$delayDuration = _ref5.delayDuration,
    delayDuration = _ref5$delayDuration === void 0 ? 500 : _ref5$delayDuration;
  return /*#__PURE__*/React.createElement(RadixTooltip.Provider, {
    delayDuration: delayDuration
  }, children);
};
Tooltip.Provider = Provider;

export { Accordion, AccordionContent, AccordionHeading, Alert, AspectRatio, Banner, Box, BulletedList, SimpleCarousel as Carousel, CarouselControls, CarouselItem, CarouselNextButton, CarouselPreviousButton, CarouselRoot, CarouselScrollArea, CarouselState, Chip, Container, CustomLink, Divider, Emoji, Flex, FocusIndicator, GlobalStyle, GlobalStyleWithTokens, Grid, Heading, HeartToggle, IconButton, IconList, IconListItem, Image, InfoStrip, Link, LinkAsProvider, LoadingIndicator, Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay, NumberedList, OptionsMenu, OurPick, PageError, Pill, Popover, PrimaryButton, ProgressIndicator, Rating, RichTextContainer, ScreenReaderOnly, SecondaryButton, SideModal, SimpleCarousel, SkeletonBox, StepStatus, Strip, StyledCarouselNextButton, Tab, LabelledTabList as TabList, StyledTabPanel as TabPanel, StyledTabPanels as TabPanels, Root as Tabs, TertiaryButton, Text, TextLink, Tooltip, UnstyledCarouselControls, UnstyledCarouselNextButton, UnstyledCarouselPreviousButton, UnstyledCarouselRoot, UnstyledCarouselScrollArea, VerticalStack as VStack, VerticalStack, carouselShallowCompare, focusIndicatorStyles, useCarousel };
